/**
 * ===== 型定義同期ガイドライン =====
 * 型ファイルは下記2つの同期された型ファイルが存在します。
 *  - **フロントエンド**: `frontend-shared/src/types/index.ts`
 *  - **バックエンド**: `backend/src/types/index.ts`
 * 
 * 【基本原則】この/types/index.tsを更新したら、もう一方の/types/index.tsも必ず同じ内容に更新する
 * 
 * 【変更の責任】
 * - 型定義を変更した開発者は、両方のファイルを即座に同期させる責任を持つ
 * - 1つのtypes/index.tsの更新は禁止。必ず1つを更新したらもう一つも更新その場で行う
 * 
 * 【絶対に守るべき原則】
 * 1. フロントエンドとバックエンドで異なる型を作らない
 * 2. 同じデータ構造に対して複数の型を作らない
 * 3. 新しいプロパティは必ずオプショナルとして追加
 * 4. APIパスは必ずこのファイルで一元管理する
 * 5. コード内でAPIパスをハードコードしない
 * 6. 2つの同期されたtypes/index.tsを単一の真実源とする
 * 7. パスパラメータを含むエンドポイントは関数として提供する
 *
 * 【フロントエンドはSSOT方式】
 * - frontend-shared/src/types/index.ts が唯一の真実源
 * - 他の3箇所（buyer/seller/admin）はこのファイルをre-exportするのみ
 * - 型定義の追加・変更はこのファイルのみで行う
 *
 * 【re-export先】
 * - frontend-buyer/src/types/index.ts → export * from '@shared/types';
 * - frontend-seller/src/types/index.ts → export * from '@shared/types';
 * - frontend-admin/src/types/index.ts → export * from '@shared/types';
 */

// ===== 基本型定義 =====
export type ID = string;

// 多言語対応
export type MultiLanguageText = Record<string, string | undefined> & {
  ja: string;
  ko?: string;
  en?: string;
  zh?: string;
  // 将来対応予定
  // es?: string;
  // fr?: string;
  // pt?: string;
}

// 言語型
export type Language = 'ja' | 'ko' | 'en' | 'zh';

// 言語enum（後方互換性のため保持）
export enum LanguageEnum {
  JA = 'ja', // 日本語
  KO = 'ko', // 韓国語
  EN = 'en', // 英語
  ZH = 'zh', // 中国語
  // 将来対応予定言語
  // ZH_CN = 'zh-CN', // 中国語簡体字
  // ZH_TW = 'zh-TW', // 中国語繁体字
  // ES = 'es', // スペイン語
  // FR = 'fr', // フランス語
  // PT = 'pt', // ポルトガル語
}
export type Currency = 'JPY' | 'USD' | 'KRW';

// タイムスタンプ関連
export interface Timestamps {
  createdAt: Date;
  updatedAt: Date;
}

// API共通レスポンス
export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
  meta?: {
    page?: number;
    limit?: number;
    total?: number;
    totalPages?: number;
  };
}

// ページネーション
export interface PaginationParams {
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
}

export interface PaginatedResponse<T> {
  items: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  // 直接アクセス用（paginationのショートカット）
  total?: number;
  limit?: number;
  page?: number;
  totalPages?: number;
  offset?: number;
}

// ===== 購入者関連の型定義 =====
export enum UserRole {
  GUEST = 'guest',        // 認証なしの一般訪問者
  BUYER = 'buyer',
  SELLER = 'seller',
  ADMIN = 'admin',
  SUPER_ADMIN = 'super_admin',
  EDITOR = 'editor',
  VIEWER = 'viewer',
}

export interface User extends Timestamps {
  id: ID;
  email: string;
  firstName?: string;
  lastName?: string;
  role: UserRole;
  language: Language;
  currency?: Currency;
  country?: string;
  phoneNumber?: string;
  avatar?: string;
  isActive?: boolean;
  isEmailVerified?: boolean;
  emailVerified?: boolean;
  phoneVerified?: boolean;
  status?: 'active' | 'suspended' | 'banned' | 'pending_verification' | 'pending_approval';
  lastLoginAt?: Date;
  registrationDate?: Date;
  // 設定関連フィールド
  twoFactorEnabled?: boolean;
  notificationSettings?: {
    orderStatus: boolean;
    promotions: boolean;
    productUpdates: boolean;
    push: boolean;
  };
}

export interface UserProfile {
  id: ID;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  avatar?: string;
  language: Language;
  currency: Currency;
  timezone?: string;
  dateFormat?: string;
}

// 認証関連
export interface LoginRequest {
  email: string;
  password: string;
  remember?: boolean;
}

export interface LoginResponse {
  user: User;
  accessToken: string;
  refreshToken?: string;
  expiresIn: number;
}

export interface RegisterRequest {
  email: string;
  password: string;
  confirmPassword: string;
  language?: Language;
}

// ソーシャルログイン
export interface SocialLoginRequest {
  provider: 'google' | 'apple';
  idToken: string;
}

export interface SocialLoginResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
  isNewUser: boolean;
}

export interface GoogleUserInfo {
  sub: string;
  email: string;
  emailVerified: boolean;
  name?: string;
  givenName?: string;
  familyName?: string;
  picture?: string;
  locale?: string;
}

// 管理者認証
export interface AdminUser extends User {
  role: UserRole.ADMIN | UserRole.SUPER_ADMIN | UserRole.EDITOR | UserRole.VIEWER;
  permissions: string[];
  twoFactorEnabled: boolean;
  lastAccessIp?: string;
  failedLoginAttempts: number;
  lockedUntil?: Date;
  forcePasswordChange?: boolean;
}

export interface AdminLoginRequest {
  email: string;
  password: string;
  remember?: boolean;
  twofaToken?: string;
}

export interface AdminLoginResponse {
  user: AdminUser;
  accessToken: string;
  refreshToken?: string;
  requiresTwoFactor: boolean;
  twoFactorToken?: string;
  expiresIn: number;
}

export interface AdminLoginOtpRequiredResponse {
  requiresTwoFa: boolean;
  resendToken: string;
}

export interface AdminResendOtpRequest {
  email: string;
  resendToken: string;
}

export interface AdminResendOtpResponse {
  sent: boolean;
  resendToken: string;
}

export interface TwoFactorVerifyRequest {
  twoFactorToken: string;
  code: string;
}

// Seller認証
export interface SellerLoginRequest {
  email: string;
  password: string;
  remember?: boolean;
  twofaToken?: string;
}

export interface SellerLoginResponse {
  seller: {
    id: ID;
    email: string;
    businessName?: MultiLanguageText;
    status: string;
    commissionRate?: number;
    role: UserRole;
    twofaEnabled?: boolean;
  };
  accessToken: string;
  refreshToken?: string;
  expiresIn: number;
}

export interface SellerLoginOtpRequiredResponse {
  requiresTwoFa: true;
  resendToken: string;
}

export interface SellerResendOtpRequest {
  email: string;
  resendToken: string;
}

export interface SellerResendOtpResponse {
  resendToken: string;
}

export interface SellerAuthResponse {
  id: ID;
  email: string;
  firstName?: string;
  lastName?: string;
  businessName?: MultiLanguageText;
  status: string;
  twofaEnabled: boolean;
  role: UserRole;
}

// ===== 販売者関連の型定義 =====
export interface Seller extends Timestamps {
  id: ID;
  companyName: string;
  businessRegistrationNumber?: string;
  contactEmail: string;
  representativeName: string;
  phoneNumber: string;
  address: Address;
  websiteUrl?: string;
  businessType?: string;
  status: SellerStatus;
  contractStartDate: Date;
  contractEndDate?: Date;
  commissionRate: number;
  monthlySales: number;
  productCount: number;
  registrationDate: Date;
  rating: number;
  reviewCount: number;
  [key: string]: unknown;
}

export enum SellerStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
  PENDING_APPROVAL = 'pending_approval',
}

export interface SellerSettings {
  sellerId: ID;
  autoAcceptOrders: boolean;
  processingDays: number;
  shippingMethods: string[];
  returnPolicy?: string;
  businessHours?: string;
  holidaySchedule?: string[];
  [key: string]: unknown;
}

export interface SellerProduct extends Timestamps {
  id: ID;
  sellerId: ID;
  name: string;
  description?: string;
  categoryId: ID;
  sku: string;
  price: number;
  imageUrls: string[];
  status: ProductStatus;
  sales: number;
  revenue: number;
  stock: number;
  stockQuantity: number;
  stockLevel: string;
  stockStatus: StockStatus;
  trend: string;
  trendValue: number;
  isActive: boolean;
  [key: string]: unknown;
}

export interface SellerListItem {
  id: ID;
  companyName: string;
  representativeName: string;
  contactEmail: string;
  phoneNumber: string;
  status: SellerStatus;
  productCount: number;
  monthlySales: number;
  registrationDate: Date;
  rating: number;
  reviewCount: number;
  [key: string]: unknown;
}

// ===== 商品関連の型定義 =====
export enum ProductStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  DRAFT = 'draft',
  OUT_OF_STOCK = 'out_of_stock',
  SUSPENDED = 'suspended',
  DELETED = 'deleted',
}

export enum StockStatus {
  IN_STOCK = 'in_stock',
  LOW_STOCK = 'low_stock',
  OUT_OF_STOCK = 'out_of_stock',
  BACKORDER = 'backorder',
}

export interface Product extends Timestamps {
  id: ID;
  sku: string;
  productCode: string;
  sellerId: ID;
  sellerName?: MultiLanguageText | string;
  sellerRating?: number;
  sellerReviewCount?: number;
  sellerProfileImage?: string;
  title: MultiLanguageText;
  description: MultiLanguageText;
  specifications?: MultiLanguageText;
  price: number;
  originalPrice?: number;
  wholesalePrice?: number;
  costPrice?: number;
  currency: Currency;
  categoryId?: string;
  categoryName?: MultiLanguageText;
  tags: string[];
  images: string[];
  weight?: number;
  dimensions?: {
    width: number;
    height: number;
    depth: number;
  };
  brand?: string;
  manufacturer?: string;
  originCountry: MultiLanguageText;
  manufacturingRegion?: string;
  material?: string;
  janCode?: string;
  rating: number;
  reviewCount: number;
  status: ProductStatus;
  stockStatus: StockStatus;
  currentStock: number;
  lowStockThreshold: number;
  /** 倉庫管理フラグ: true=Inventoryレコードあり・在庫数連動 / false=products.current_stockのみ */
  isWarehouseManaged: boolean;
  purchaseLimit?: number;
  isFeatured?: boolean;
  isNewArrival?: boolean;
  shippingMethods?: Record<string, boolean>;
  shippingNotes?: string;
  variants?: ProductVariant[];
  variantAxes?: Array<{ name: Record<string, string>; values: Array<Record<string, string>> }>;
  // 自動取込関連
  sourcePlatform?: 'mercari' | 'yauction' | 'normal';
  sourceId?: string;
  priceRuleId?: ID;
  // 割引関連
  discountRate?: number;
  discountPrice?: number;
  // 商品状態
  condition?: 'new' | 'like_new' | 'excellent' | 'good' | 'acceptable' | 'damaged';
  // お気に入り数
  likesCount?: number;
  availability?: 'in_stock' | 'low_stock' | 'sold' | 'available' | 'unavailable';
}

export interface ProductVariant {
  id: ID;
  productId: ID;
  variantSku: string;
  colorOption?: string;
  sizeOption?: string;
  price?: number;
  stock: number;
  images?: string[];
  attributes?: Array<{ axisName: Record<string, string>; value: Record<string, string> }>;
  displayOrder: number;
}

/**
 * 在庫管理用商品バリエーション
 * inventory.service.ts専用の型定義
 * ProductVariantとは異なる構造を持つ
 */
export interface InventoryProductVariant {
  id: ID;
  productId: ID;
  sku: string;
  name: string;
  price: number;
  stock: number;
  attributes: Record<string, string>;
}

export interface CreateProductRequest {
  sku?: string;
  name: MultiLanguageText;
  description: MultiLanguageText;
  price: number;
  currency: Currency;
  category: string;
  subCategory?: string;
  tags?: string[];
  images: string[];
  weight?: number;
  dimensions?: {
    width: number;
    height: number;
    depth: number;
  };
  brand?: string;
  manufacturer?: string;
  originCountry: MultiLanguageText;
  manufacturingRegion?: string;
  material?: string;
  currentStock: number;
  lowStockThreshold?: number;
  variants?: Omit<ProductVariant, 'id' | 'productId'>[];
}

export interface UpdateProductRequest extends Partial<CreateProductRequest> {
  status?: ProductStatus;
}

// カテゴリー
export interface Category extends Timestamps {
  id: ID;
  name: MultiLanguageText;
  parentId?: ID;
  level: number;
  sortOrder: number;
  isActive: boolean;
  image?: string;
  icon?: string; // カテゴリーアイコン
}

// ===== 在庫管理関連の型定義 =====
export interface Inventory extends Timestamps {
  id: ID;
  productId: ID;
  sku: string;
  currentStock: number;
  reservedStock: number;
  availableStock: number;
  lowStockThreshold: number;
  stockStatus: StockStatus;
  warehouseLocation?: string;
  lastStockUpdate: Date;
}

export interface StockMovement extends Timestamps {
  id: ID;
  inventoryId?: ID;
  productId: ID;
  movementType: StockMovementType;
  quantity: number;
  previousStock: number;
  newStock: number;
  reason?: string;
  orderId?: ID;
  userId?: ID;
  image: string;
  supplier: string;
  date: string;
  time: string;
  actionType: string;
  quantityChange: number;
}

export enum StockMovementType {
  INCOMING = 'incoming',
  OUTGOING = 'outgoing',
  ADJUSTMENT = 'adjustment',
  RESERVED = 'reserved',
  RELEASED = 'released',
}

export interface StockAlert {
  id: ID;
  productId: ID;
  sku: string;
  alertType: 'low_stock' | 'out_of_stock';
  currentStock: number;
  threshold: number;
  isResolved: boolean;
  createdAt: Date;
}

export interface InventoryItem extends Timestamps {
  id: ID;
  productId: ID;
  sku: string;
  productName: string;
  imageUrl: string;
  category: string;
  currentStock: number;
  stock: number;
  reservedStock: number;
  availableStock: number;
  lowStockThreshold: number;
  status: string;
  stockStatus: StockStatus;
  warehouseLocation?: string;
  lastStockUpdate: Date;
  expectedArrival: Date | null;
  updatedAt: Date;
}

// ===== カート関連の型定義 =====
export interface CartItem extends Timestamps {
  id: ID;
  userId: ID;
  productId: ID;
  productName: string;
  sku: string;
  sellerName: string;
  sellerId: ID;
  quantity: number;
  unitPrice: number;
  subtotal: number;
  currency: Currency;
  addedAt: Date;
  // CartPage互換フィールド（既存実装との互換性維持）
  title: string;
  price: number;
  maxQuantity: number;
  image: string;
  stockStatus: StockStatus;
  stockCount: number;
  variants?: {
    color?: string;
    size?: string;
    attributes?: ProductVariant['attributes'];
    [key: string]: string | ProductVariant['attributes'] | undefined;
  };
}

// ===== 注文関連の型定義 =====
export enum OrderStatus {
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
  PROCESSING = 'processing',
  SHIPPED = 'shipped',
  DELIVERED = 'delivered',
  CANCELLED = 'cancelled',
  REFUNDED = 'refunded',
  RETURNED = 'returned',
}

export enum PaymentStatus {
  PENDING = 'pending',
  PAID = 'paid',
  FAILED = 'failed',
  REFUNDED = 'refunded',
  PARTIALLY_REFUNDED = 'partially_refunded',
}

export interface Order extends Timestamps {
  id: ID;
  orderNumber: string;
  userId: ID;
  buyerInfo: BuyerInfo;
  items: OrderItem[];
  subtotal: number;
  shippingFee: number; // バックエンドAPI対応（Order.to_dict()が常に数値を返す）
  total: number; // バックエンドAPI対応（Order.to_dict()が常に数値を返す）
  currency: Currency;
  status: OrderStatus;
  paymentStatus: PaymentStatus;
  paymentMethod: string;
  shippingAddress: OrderShippingAddress;
  billingAddress?: Address;
  shippingMethod: string;
  shippingInsuranceFee: number; // 配送保険料（Order.to_dict()が常に数値を返す）
  photoOptionFee: number; // 撮影オプション料金（Order.to_dict()が常に数値を返す）
  serviceFee: number; // 手数料（購入代行金額確定時に発生。Order.to_dict()が常に数値を返す）
  couponDiscount?: number; // クーポン割引額
  couponCode?: string; // 適用クーポンコード
  exchangeRate?: number; // 注文確定時のJPY→USD為替レート
  trackingNumber?: string;
  carrier?: string; // 配送業者
  cardLast4?: string; // カード下4桁
  cardBrand?: string; // カードブランド
  estimatedDeliveryDate?: Date;
  estimatedDaysMin?: number; // 最小配送日数
  estimatedDaysMax?: number; // 最大配送日数
  actualDeliveryDate?: Date;
  orderDate: Date;
  notes?: string;
  secondaryPaymentStatus?: string; // 二次決済状態（pending/paid/not_required）
  pendingPaymentRequestNumber?: string; // 未払いの二次決済請求番号
  isFormRequest?: boolean; // 購入代行依頼フォーム発の注文か
  actualWeightKg?: number | null; // 実測重量（kg）
  actualLengthCm?: number | null; // 実測サイズ・長さ（cm）
  actualWidthCm?: number | null; // 実測サイズ・幅（cm）
  actualHeightCm?: number | null; // 実測サイズ・高さ（cm）
}

export interface PaymentRequestItem {
  id: ID;
  label: string;
  amount: number;
  sortOrder: number;
}

export interface PaymentRequestShippingCandidate {
  methodId: string;
  methodName?: Record<string, string>;
  carrierName?: Record<string, string>;
  estimatedDaysMin?: number;
  estimatedDaysMax?: number;
  totalPrice: number;
  difference: number;
  currency?: string;
}

export interface PaymentRequest extends Timestamps {
  id: ID;
  requestNumber: string;
  requestType: 'shipping_difference';
  status: 'pending' | 'paid' | 'not_required' | 'cancelled';
  orderId?: ID;
  userId: ID;
  title?: string;
  description?: string;
  currency: string;
  baseShippingFee?: number;
  candidateShippingMethods?: PaymentRequestShippingCandidate[];
  selectedShippingMethodId?: ID;
  amount?: number;
  dueDate?: string;
  paymentId?: ID;
  createdBy?: ID;
  paidAt?: string;
  cancelledAt?: string;
  items: PaymentRequestItem[];
  orderNumber?: string;
  orderCreatedAt?: string;
  orderStatus?: string;
  orderItems?: OrderItem[];
  actualWeightKg?: number;
  actualLengthCm?: number;
  actualWidthCm?: number;
  actualHeightCm?: number;
  shippingAddress?: OrderShippingAddress;
  shippingInsuranceFee?: number;
  photos?: string[];
}

export interface OrderItem {
  id: ID;
  orderId: ID;
  productId: ID;
  productCode?: string;
  sku: string;
  productName: string;
  productImage: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
  currency: Currency;
  variantInfo?: {
    color?: string;
    size?: string;
    attributes?: ProductVariant['attributes'];
    [key: string]: string | ProductVariant['attributes'] | undefined;
  };
  variantSnapshot?: {
    colorOption?: string;
    sizeOption?: string;
    attributes?: ProductVariant['attributes'];
  };
  sellerInfo: {
    sellerId: ID;
    sellerName: string;
  };
  giftOptions?: {
    isGift: boolean;
    giftMessage?: string;
    giftWrap?: boolean;
  };
  productSnapshot?: {
    name?: string;
    productUrl?: string;
    note?: string;
    thumbnailUrl?: string;
    sku?: string;
    type?: string;
    [key: string]: unknown;
  };
}

export interface BuyerInfo {
  id: ID;
  name: string;
  email: string;
  phone?: string;
}

export interface CreateOrderRequest {
  items: {
    productId: ID;
    quantity: number;
    variantInfo?: {
      color?: string;
      size?: string;
      [key: string]: string | undefined;
    };
  }[];
  shippingAddress: Address;
  billingAddress?: Address;
  shippingMethod: string;
  paymentMethod: string;
  currency: Currency;
  notes?: string;
}

export interface SellerOrder extends Timestamps {
  id: ID;
  orderNumber: string;
  userId: ID;
  buyerId: ID;
  buyerName: string;
  email: string;
  buyerEmail?: string;
  date: Date | string;
  items: OrderItem[];
  subtotal: number;
  shippingCost: number;
  taxAmount: number;
  totalAmount: number;
  amount: number;
  currency: Currency;
  status: OrderStatus;
  orderStatus: string;
  paymentStatus: string;
  paymentMethod: string;
  shippingAddress: Address;
  trackingNumber?: string;
  [key: string]: unknown;
}

// ===== カート関連の型定義（Cart追加） =====
export interface Cart {
  userId: ID;
  items: CartItem[];
  subtotal: number;
  currency: Currency;
  lastUpdated: Date;
}

export interface AddToCartRequest {
  productId: ID;
  quantity: number;
  variants?: {
    color?: string;
    size?: string;
  };
}

export interface UpdateCartItemRequest {
  quantity: number;
}

// ===== 決済関連の型定義 =====
export enum PaymentMethod {
  CREDIT_CARD = 'credit_card',
  PAYPAL = 'paypal',
  ALIPAY = 'alipay',
  WECHAT_PAY = 'wechat_pay',
  BANK_TRANSFER = 'bank_transfer',
  CONVENIENCE_STORE = 'convenience_store',
}

export enum ShippingDisplayScope {
  DOMESTIC = 'domestic',
  INTERNATIONAL = 'international',
  HIDDEN = 'hidden',
}

export interface Payment extends Timestamps {
  id: ID;
  orderId: ID;
  buyerId: ID;
  buyerName: string;
  buyerEmail: string;
  amount: number;
  currency: Currency;
  paymentMethod: PaymentMethod;
  paymentProvider: string;
  status: PaymentStatus;
  transactionId?: string;
  gatewayResponse?: Record<string, unknown>;
  riskScore?: number;
  fraudFlags?: string[];
  refundAmount?: number;
  refundReason?: string;
}

export interface PaymentSettings {
  paymentMethod: PaymentMethod;
  enabled: boolean;
  providerConfig: Record<string, unknown>;
  commissionRate: number;
  fraudDetectionRules?: Record<string, unknown>;
  minimumAmount?: number;
  maximumAmount?: number;
}

export interface ProcessPaymentRequest {
  orderId: ID;
  paymentMethod: PaymentMethod;
  amount: number;
  currency: Currency;
  paymentDetails: Record<string, unknown>;
  cardholderName?: string;
}

export interface RefundRequest {
  paymentId: ID;
  amount: number;
  reason: string;
}

export interface PaymentScheduleItem {
  id: ID;
  orderId: ID;
  orderNumber: string;
  sellerId: ID;
  sellerName: string;
  amount: number;
  currency: Currency;
  status: PaymentStatus;
  scheduledDate: Date;
  processedDate?: Date;
  reference: string;
  paymentMethod?: PaymentMethod;
  notes?: string;
}

// ===== 管理者専用決済管理の型定義 =====

// 管理者専用決済設定
export interface AdminPaymentSettings {
  defaultCurrency: string;
  enabledMethods: string[];
  autoRefundEnabled: boolean;
  refundPeriodDays: number;
  stripeEnabled: boolean;
  paypalEnabled: boolean;
  updatedAt?: string;
  updatedBy?: string;
}

export interface AdminPaymentSettingsUpdate {
  defaultCurrency?: string;
  enabledMethods?: string[];
  autoRefundEnabled?: boolean;
  refundPeriodDays?: number;
  stripeEnabled?: boolean;
  paypalEnabled?: boolean;
}

// 決済プロバイダー
export interface PaymentProvider {
  id: ID;
  name: string;
  type: string;
  enabled: boolean;
  apiKey?: string;
  apiKeyMasked?: string;
  webhookUrl?: string;
  config: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
}

export interface PaymentProviderCreate {
  name: string;
  type: string;
  enabled: boolean;
  apiKey?: string;
  webhookUrl?: string;
  config?: Record<string, unknown>;
}

export interface PaymentProviderUpdate {
  name?: string;
  type?: string;
  enabled?: boolean;
  apiKey?: string;
  webhookUrl?: string;
  config?: Record<string, unknown>;
}

// ===== 配送関連の型定義 =====
export interface OrderShippingAddress {
  name?: string;
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  phone?: string;
  personalCustomsCode?: string;
}

export interface Address {
  id?: ID;
  userId?: ID;
  addressType?: 'home' | 'office' | 'other';
  name?: string; // バックエンドAPI対応
  country: string;
  stateProvince?: string;
  state?: string; // バックエンドAPI対応
  city: string;
  postalCode: string;
  addressLine1: string;
  addressLine2?: string;
  phone?: string; // バックエンドAPI対応
  isDefault?: boolean;
  personalCustomsCode?: string; // 韓国個人通関固有符号 (PCCC)
}


export interface ShippingInfo {
  orderId: ID;
  shippingAddress: Address;
  billingAddress?: Address;
  shippingMethod: ShippingMethod;
  carrier: string;
  trackingNumber?: string;
  estimatedDelivery?: Date;
  actualDelivery?: Date;
  hsCode?: string;
  customsCategory?: string;
  shippingEvents?: ShippingEvent[];
}

export interface ShippingEvent {
  timestamp: Date;
  status: string;
  location?: string;
  description: string;
}

export interface Carrier {
  id: ID;
  carrierName: string;
  integrationMethod: string;
  coverageArea: string[];
  trackingUrlTemplate: string;
  connectionStatus: 'connected' | 'disconnected' | 'error';
  lastSync?: Date;
}

// ===== 自動取込関連の型定義 =====
export interface ImportSetting extends Timestamps {
  id: ID;
  sellerId: ID;
  platform: 'mercari' | 'yauction';
  isEnabled: boolean;
  syncInterval: number; // minutes
  lastSyncAt?: Date;
  rules: ImportRule[];
  credentials?: Record<string, unknown>; // encrypted
}

export interface ImportRule {
  id: ID;
  ruleType: 'category_mapping' | 'price_markup' | 'stock_sync' | 'status_filter';
  conditions: Record<string, unknown>;
  actions: Record<string, unknown>;
  isActive: boolean;
}

export interface PriceRule extends Timestamps {
  id: ID;
  sellerId: ID;
  name: string;
  markupType: 'percentage' | 'fixed';
  markupValue: number;
  minimumPrice?: number;
  maximumPrice?: number;
  applyToCategories?: string[];
  isDefault: boolean;
  isActive: boolean;
}

// ===== レビュー関連の型定義 =====
export interface Review extends Timestamps {
  id: ID;
  productId: ID;
  userId: ID;
  orderId?: ID;
  rating: number; // 1-5
  title?: string;
  comment?: string;
  images?: string[];
  isVerifiedPurchase: boolean;
  isVisible: boolean;
  language: Language;
  helpfulCount: number;
  reportCount: number;
  // 拡張フィールド
  userName?: string;
  userAvatar?: string;
  productName?: string;
  productImage?: string;
  sellerReply?: ReviewReply;
  variant?: {
    color?: string;
    size?: string;
    [key: string]: string | undefined;
  };
  helpfulUsers?: ID[];
  reportUsers?: ID[];
  moderationStatus?: 'approved' | 'pending' | 'rejected';
  moderationNotes?: string;
}

export interface ReviewReply extends Timestamps {
  id: ID;
  reviewId: ID;
  sellerId: ID;
  sellerName: string;
  message: string;
  isVisible: boolean;
}

export interface CreateReviewRequest {
  productId: ID;
  orderId?: ID;
  rating: number;
  title?: string;
  comment?: string;
  images?: string[];
  videos?: string[];
  variant?: {
    color?: string;
    size?: string;
    [key: string]: string | undefined;
  };
}

export interface UpdateReviewRequest {
  rating?: number;
  title?: string;
  comment?: string;
  images?: string[];
  videos?: string[];
}

export interface ReviewFilters {
  productId?: ID;
  rating?: number;
  hasImages?: boolean;
  verifiedPurchase?: boolean;
  dateRange?: {
    from: Date;
    to: Date;
  };
  sortBy?: 'newest' | 'oldest' | 'highest_rating' | 'lowest_rating' | 'most_helpful';
  searchKeyword?: string;
}

export interface ReviewStats {
  totalReviews: number;
  averageRating: number;
  ratingDistribution: {
    1: number;
    2: number;
    3: number;
    4: number;
    5: number;
  };
  verifiedPurchaseCount: number;
  withImagesCount: number;
}

// 注文統計
export interface OrderStats {
  totalOrders: number;
  pendingOrders: number;
  shippedOrders: number;
  deliveredOrders: number;
  cancelledOrders: number;
  totalRevenue: number;
  averageOrderValue: number;
  todayOrders: number;
  weeklyGrowth: number;
}

// 顧客統計
export interface BuyerStats {
  totalBuyers: number;
  activeBuyers: number;
  newBuyers?: number;
  newBuyersThisMonth?: number;
  premiumBuyers?: number;
  buyerSatisfaction?: number;
  averageLifetimeValue: number;
  averageOrdersPerBuyer?: number;
  retentionRate?: number;
  churnRate: number;
  topCountries?: Array<{
    country: string;
    count: number;
    percentage: number;
  }>;
  buyerGrowth?: Array<{
    month: string;
    buyers: number;
    growth: number;
  }>;
}

// 顧客セグメント
export interface BuyerSegment {
  id: ID;
  name: string;
  description: string;
  customerCount: number;
  averageSpend: number;
  criteria: {
    minOrders?: number;
    maxOrders?: number;
    minSpend?: number;
    maxSpend?: number;
    tier?: BuyerTier;
    status?: BuyerStatus;
    registeredAfter?: Date;
    registeredBefore?: Date;
  };
}

export interface BuyerSegmentStats {
  vip: {
    count: number;
    percentage: number;
    averageOrderValue: number;
    totalRevenue: number;
  };
  premium: {
    count: number;
    percentage: number;
    averageOrderValue: number;
    totalRevenue: number;
  };
  regular: {
    count: number;
    percentage: number;
    averageOrderValue: number;
    totalRevenue: number;
  };
  new: {
    count: number;
    percentage: number;
    averageOrderValue: number;
    totalRevenue: number;
  };
  dormant: {
    count: number;
    percentage: number;
    averageOrderValue: number;
    totalRevenue: number;
  };
}

// サポート履歴
export interface SupportTicket extends Timestamps {
  id: ID;
  buyerId: ID;
  ticketNumber?: string;
  subject: string;
  description: string;
  category: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  assignedTo?: ID;
  resolutionTime?: number; // minutes
  buyerSatisfaction?: number; // 1-5
  tags: string[];
}

// 売上統計
export interface SalesStats {
  totalSales: number;
  totalOrders: number;
  averageOrderValue: number;
  growthRate?: number;
  topProduct?: string;
  newBuyers?: number;
  topSellingProducts: {
    productId: ID;
    productName: string;
    salesCount: number;
    revenue: number;
  }[];
  salesByCategory: {
    category: string;
    sales: number;
    orderCount: number;
  }[];
  salesByRegion: {
    region: string;
    sales: number;
    orderCount: number;
  }[];
  monthlySales: {
    month: string;
    sales: number;
    orderCount: number;
    growthRate: number;
  }[];
  dailySales: {
    date: string;
    sales: number;
    orderCount: number;
  }[];
}

// 比較分析
export interface SalesComparison {
  current?: {
    sales: number;
    orderCount?: number;
    orders?: number;
    averageOrderValue: number;
  };
  currentYear?: {
    sales: number;
    orders: number;
    averageOrderValue: number;
  };
  currentMonth?: {
    sales: number;
    orders: number;
    averageOrderValue: number;
  };
  previous: {
    sales: number;
    orderCount?: number;
    orders?: number;
    averageOrderValue: number;
  };
  previousYear?: {
    sales: number;
    orders: number;
    averageOrderValue: number;
  };
  previousMonth?: {
    sales: number;
    orders: number;
    averageOrderValue: number;
  };
  growthRate?: {
    sales: number;
    orderCount?: number;
    orders?: number;
    averageOrderValue: number;
  };
  growth?: {
    salesGrowth: number;
    ordersGrowth: number;
    averageOrderValueGrowth: number;
  };
}

// 利益分析
export interface ProfitAnalysis {
  totalRevenue?: number;
  totalCost?: number;
  grossRevenue?: number;
  costOfGoodsSold?: number;
  operatingExpenses?: number;
  grossProfit: number;
  netProfit: number;
  profitMargin?: number;
  grossMargin?: number;
  netMargin?: number;
  costBreakdown?: {
    productCost?: number;
    productCosts?: number;
    shippingCost?: number;
    shippingCosts?: number;
    platformFee?: number;
    paymentProcessing?: number;
    marketingCost?: number;
    marketingCosts?: number;
    operationalCost?: number;
    operationalCosts?: number;
  };
  breakdown?: {
    productCosts: number;
    shippingCosts: number;
    paymentProcessing: number;
    marketingCosts: number;
    operationalCosts: number;
  };
}

// 予測データ
export interface SalesForecast {
  date?: string;
  month?: string;
  predictedSales?: number;
  predicted?: number;
  confidence: number;
}

// 商品売上データ
export interface ProductSalesData {
  productId: ID;
  productName: string;
  salesCount: number;
  salesAmount?: number;
  revenue: number;
  profit?: number;
  growthRate?: number;
  unitsSold?: number;
  orderCount?: number;
}

export interface ReviewHelpfulRequest {
  reviewId: ID;
  helpful: boolean;
}

export interface CreateReviewReplyRequest {
  reviewId: ID;
  message: string;
}

// ===== チャットサポート関連の型定義 =====
export enum ChatRoomStatus {
  WAITING = 'waiting',
  ACTIVE = 'active',
  CLOSED = 'closed',
  RESOLVED = 'resolved',
}

export enum ChatMessageType {
  TEXT = 'text',
  IMAGE = 'image',
  FILE = 'file',
  SYSTEM = 'system',
  AUTO_RESPONSE = 'auto_response',
}

export enum ChatCategory {
  GENERAL = 'general',
  ORDER_INQUIRY = 'order_inquiry',
  PRODUCT_INQUIRY = 'product_inquiry',
  PAYMENT_ISSUE = 'payment_issue',
  SHIPPING_INQUIRY = 'shipping_inquiry',
  RETURN_REFUND = 'return_refund',
  TECHNICAL_SUPPORT = 'technical_support',
  ACCOUNT_ISSUE = 'account_issue',
}

export enum AgentStatus {
  ONLINE = 'online',
  BUSY = 'busy',
  AWAY = 'away',
  OFFLINE = 'offline',
}

export interface ChatRoom extends Timestamps {
  id: ID;
  userId: ID;
  agentId?: ID;
  status: ChatRoomStatus;
  category: ChatCategory;
  subject: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  language: Language;
  lastMessageAt: Date;
  messageCount: number;
  unreadCount: number;
  satisfactionRating?: number;
  satisfactionComment?: string;
  isArchived: boolean;
  tags: string[];
}

export interface ChatMessage extends Timestamps {
  id: ID;
  roomId: ID;
  senderId: ID;
  senderType: 'buyer' | 'agent' | 'system';
  message: string;
  messageType: ChatMessageType;
  isRead: boolean;
  replyToId?: ID;
  attachments?: ChatAttachment[];
  metadata?: Record<string, unknown>;
}

export interface ChatAttachment {
  id: ID;
  messageId: ID;
  fileName: string;
  fileSize: number;
  fileType: string;
  filePath: string;
  url?: string;
  thumbnailPath?: string;
  uploadedAt: Date;
}

export interface ChatAgent extends Timestamps {
  id: ID;
  userId: ID;
  name: string;
  email: string;
  avatar?: string;
  status: AgentStatus;
  department: string;
  languages: Language[];
  specialties: string[];
  currentRooms: number;
  maxRooms: number;
  averageResponseTime: number;
  averageRating?: number;
  buyerRating: number;
  isOnline: boolean;
  lastActiveAt: Date;
}

export interface ChatSession extends Timestamps {
  id: ID;
  userId: ID;
  adminId?: ID;
  status: 'waiting' | 'active' | 'closed';
  topic?: string;
  language: Language;
  priority: 'low' | 'medium' | 'high';
  lastMessageAt: Date;
}

export interface AutoResponse {
  id: ID;
  trigger: string;
  response: MultiLanguageText;
  category: ChatCategory;
  isActive: boolean;
  priority: number;
}

export interface QuickReply {
  id: ID;
  text: MultiLanguageText;
  category: ChatCategory;
  isActive: boolean;
  sortOrder: number;
}

export interface ChatTypingIndicator {
  roomId: ID;
  userId: ID;
  userName: string;
  isTyping: boolean;
  timestamp: Date;
}

// ===== 分析・レポート関連の型定義 =====
export interface SalesAnalytics {
  period: 'daily' | 'weekly' | 'monthly' | 'yearly';
  startDate: Date;
  endDate: Date;
  totalSales: number;
  totalOrders: number;
  averageOrderValue: number;
  conversionRate: number;
  topProducts: {
    productId: ID;
    productName: string;
    salesAmount: number;
    orderCount: number;
  }[];
  salesByCategory: {
    category: string;
    salesAmount: number;
    orderCount: number;
  }[];
  salesByCountry: {
    country: string;
    salesAmount: number;
    orderCount: number;
  }[];
}

export interface SellerSettlement extends Timestamps {
  id: ID;
  sellerId: ID;
  sellerName: string;
  period: string;
  totalSales: number;
  commissionFee: number;
  refundAmount: number;
  adjustmentAmount: number;
  settlementAmount: number;
  settlementStatus: 'pending' | 'processed' | 'paid' | 'failed';
  settlementDate?: Date;
  bankInfo: {
    accountNumber: string;
    bankName: string;
    accountHolder: string;
  };
}

export interface UserSegmentation {
  id: ID;
  name: string;
  segment: string;
  description?: string;
  percentage: number;
  userCount: number;
  criteria?: {
    [key: string]: string | undefined;
  };
}

// ===== 不正検知関連の型定義 =====
export interface FraudAlert extends Timestamps {
  id: ID;
  paymentId?: ID;
  orderId?: ID;
  userId?: ID;
  alertType: 'suspicious_payment' | 'unusual_order' | 'account_anomaly';
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  details: Record<string, unknown>;
  status: 'pending' | 'investigating' | 'resolved' | 'false_positive';
  investigatedBy?: ID;
  resolution?: string;
}

// ===== APIエンドポイント定義 =====
export const API_PATHS = {
  // 認証関連
  AUTH: {
    LOGIN: '/api/auth/login',
    LOGOUT: '/api/auth/logout',
    REGISTER: '/api/auth/register',
    REFRESH: '/api/auth/refresh',
    VERIFY_EMAIL: '/api/auth/verify-email',
    RESET_PASSWORD: '/api/auth/reset-password',
    CHANGE_PASSWORD: '/api/auth/change-password',
  },

  // 管理者認証
  ADMIN_AUTH: {
    LOGIN: '/api/admin/auth/login',
    LOGOUT: '/api/admin/auth/logout',
    REFRESH: '/api/admin/auth/refresh',
    VERIFY_2FA: '/api/admin/auth/verify-2fa',
  },

  // 購入者管理
  USERS: {
    LIST: '/api/users',
    GET: (id: ID) => `/api/users/${id}`,
    CREATE: '/api/users',
    UPDATE: (id: ID) => `/api/users/${id}`,
    DELETE: (id: ID) => `/api/users/${id}`,
    PROFILE: '/api/users/profile',
    UPDATE_PROFILE: '/api/users/profile',
    ADDRESSES: '/api/users/addresses',
    ADD_ADDRESS: '/api/users/addresses',
    UPDATE_ADDRESS: (id: ID) => `/api/users/addresses/${id}`,
    DELETE_ADDRESS: (id: ID) => `/api/users/addresses/${id}`,
  },

  // 商品管理
  PRODUCTS: {
    LIST: '/api/products',
    GET: (id: ID) => `/api/products/${id}`,
    CREATE: '/api/products',
    UPDATE: (id: ID) => `/api/products/${id}`,
    DELETE: (id: ID) => `/api/products/${id}`,
    SEARCH: '/api/products/search',
    CATEGORIES: '/api/products/categories',
    BY_CATEGORY: (categoryId: ID) => `/api/products/category/${categoryId}`,
    BY_SELLER: (sellerId: ID) => `/api/products/seller/${sellerId}`,
    VARIANTS: (productId: ID) => `/api/products/${productId}/variants`,
  },

  // 販売者管理
  SELLERS: {
    LIST: '/api/sellers',
    GET: (id: ID) => `/api/sellers/${id}`,
    CREATE: '/api/sellers',
    UPDATE: (id: ID) => `/api/sellers/${id}`,
    DELETE: (id: ID) => `/api/sellers/${id}`,
    DASHBOARD: '/api/sellers/dashboard',
    SETTINGS: '/api/sellers/settings',
    UPDATE_SETTINGS: '/api/sellers/settings',
    PRODUCTS: '/api/sellers/products',
    ORDERS: '/api/sellers/orders',
    ANALYTICS: '/api/sellers/analytics',
    SETTLEMENTS: '/api/sellers/settlements',
  },

  // 在庫管理
  INVENTORY: {
    LIST: '/api/inventory',
    GET: (id: ID) => `/api/inventory/${id}`,
    UPDATE: (id: ID) => `/api/inventory/${id}`,
    BULK_UPDATE: '/api/inventory/bulk-update',
    ALERTS: '/api/inventory/alerts',
    MOVEMENTS: '/api/inventory/movements',
    EXPORT: '/api/inventory/export',
    IMPORT: '/api/inventory/import',
  },

  // 注文管理
  ORDERS: {
    LIST: '/api/orders',
    GET: (id: ID) => `/api/orders/${id}`,
    CREATE: '/api/orders',
    UPDATE: (id: ID) => `/api/orders/${id}`,
    UPDATE_STATUS: (id: ID) => `/api/orders/${id}/status`,
    CANCEL: (id: ID) => `/api/orders/${id}/cancel`,
    TRACKING: (id: ID) => `/api/orders/${id}/tracking`,
    HISTORY: '/api/orders/history',
    BATCH_UPDATE: '/api/orders/batch-update',
  },

  // カート管理
  CART: {
    GET: '/api/cart',
    ADD_ITEM: '/api/cart/items',
    UPDATE_ITEM: (id: ID) => `/api/cart/items/${id}`,
    REMOVE_ITEM: (id: ID) => `/api/cart/items/${id}`,
    CLEAR: '/api/cart/clear',
    COUNT: '/api/cart/count',
  },

  // 決済管理
  PAYMENTS: {
    PROCESS: '/api/payments/process',
    GET: (id: ID) => `/api/payments/${id}`,
    REFUND: (id: ID) => `/api/payments/${id}/refund`,
    METHODS: '/api/payments/methods',
    SETTINGS: '/api/payments/settings',
  },

  // 配送管理
  SHIPPING: {
    METHODS: '/api/v1/admin/logistics/methods',
    CALCULATE: '/api/v1/admin/logistics/calculate',
    TRACKING: (trackingNumber: string) => `/api/v1/admin/logistics/tracking/${trackingNumber}`,
    LABELS: '/api/v1/admin/logistics/labels',
    CARRIERS: '/api/v1/admin/logistics/carriers',
  },

  // 自動取込管理
  IMPORT: {
    SETTINGS: '/api/import/settings',
    UPDATE_SETTINGS: '/api/import/settings',
    SYNC: '/api/import/sync',
    HISTORY: '/api/import/history',
    RULES: '/api/import/rules',
    PRICE_RULES: '/api/import/price-rules',
  },

  // レビュー管理
  REVIEWS: {
    LIST: '/api/reviews',
    GET: (id: ID) => `/api/reviews/${id}`,
    CREATE: '/api/reviews',
    UPDATE: (id: ID) => `/api/reviews/${id}`,
    DELETE: (id: ID) => `/api/reviews/${id}`,
    BY_PRODUCT: (productId: ID) => `/api/reviews/product/${productId}`,
    BY_USER: (userId: ID) => `/api/reviews/user/${userId}`,
  },

  // チャットサポート
  CHAT: {
    SESSIONS: '/api/chat/sessions',
    CREATE_SESSION: '/api/chat/sessions',
    GET_SESSION: (id: ID) => `/api/chat/sessions/${id}`,
    MESSAGES: (sessionId: ID) => `/api/chat/sessions/${sessionId}/messages`,
    SEND_MESSAGE: (sessionId: ID) => `/api/chat/sessions/${sessionId}/messages`,
    CLOSE_SESSION: (sessionId: ID) => `/api/chat/sessions/${sessionId}/close`,
  },

  // 管理者用API
  ADMIN: {
    DASHBOARD: '/api/admin/dashboard',
    USERS: '/api/admin/users',
    SELLERS: '/api/admin/sellers',
    PRODUCTS: '/api/admin/products',
    ORDERS: '/api/admin/orders',
    PAYMENTS: '/api/admin/payments',
    ANALYTICS: '/api/admin/analytics',
    FRAUD_ALERTS: '/api/admin/fraud-alerts',
    SETTINGS: '/api/admin/settings',
    REPORTS: '/api/admin/reports',
  },

  // 分析・レポート
  ANALYTICS: {
    SALES: '/api/analytics/sales',
    ORDERS: '/api/analytics/orders',
    BUYERS: '/api/analytics/buyers',
    PRODUCTS: '/api/analytics/products',
    INVENTORY: '/api/analytics/inventory',
    PERFORMANCE: '/api/analytics/performance',
  },

  // 代理購入管理
  PROXY_PURCHASES: {
    LIST: '/api/proxy-purchases',
    GET: (id: ID) => `/api/proxy-purchases/${id}`,
    CREATE: '/api/proxy-purchases',
    UPDATE: (id: ID) => `/api/proxy-purchases/${id}`,
    CANCEL: (id: ID) => `/api/proxy-purchases/${id}/cancel`,
    TIMELINE: (id: ID) => `/api/proxy-purchases/${id}/timeline`,
    CALCULATE: '/api/proxy-purchases/calculate',
    MESSAGES_POST: (id: ID) => `/api/proxy-purchases/${id}/messages`,
    MESSAGES_GET: (id: ID) => `/api/proxy-purchases/${id}/messages`,
    PAYMENT: (id: ID) => `/api/proxy-purchases/${id}/payment`,
    ESTIMATES: '/api/proxy-purchases/estimates',
  },

  // 外部商品管理
  EXTERNAL_PRODUCTS: {
    LIST: '/api/v1/buyer/external-products',
    GET: (id: ID) => `/api/v1/buyer/external-products/${id}`,
    SEARCH: '/api/v1/buyer/external-products/search',
    SYNC: '/api/v1/buyer/external-products/sync',
    BY_PLATFORM: (platform: string) => `/api/v1/buyer/external-products/platform/${platform}`,
  },

  // マーケティング管理
  MARKETING: {
    CAMPAIGNS: '/api/marketing/campaigns',
    GET_CAMPAIGN: (id: ID) => `/api/marketing/campaigns/${id}`,
    CREATE_CAMPAIGN: '/api/marketing/campaigns',
    UPDATE_CAMPAIGN: (id: ID) => `/api/marketing/campaigns/${id}`,
    DELETE_CAMPAIGN: (id: ID) => `/api/marketing/campaigns/${id}`,
  },

  // クーポン管理
  COUPONS: {
    LIST: '/api/coupons',
    GET: (id: ID) => `/api/coupons/${id}`,
    CREATE: '/api/coupons',
    UPDATE: (id: ID) => `/api/coupons/${id}`,
    DELETE: (id: ID) => `/api/coupons/${id}`,
    VALIDATE: '/api/coupons/validate',
    USAGE: (id: ID) => `/api/coupons/${id}/usage`,
  },

  // バナー管理
  BANNERS: {
    LIST: '/api/banners',
    GET: (id: ID) => `/api/banners/${id}`,
    CREATE: '/api/banners',
    UPDATE: (id: ID) => `/api/banners/${id}`,
    DELETE: (id: ID) => `/api/banners/${id}`,
    BY_POSITION: (position: string) => `/api/banners/position/${position}`,
  },

  // 倉庫管理
  WAREHOUSES: {
    LIST: '/api/warehouses',
    GET: (id: ID) => `/api/warehouses/${id}`,
    CREATE: '/api/warehouses',
    UPDATE: (id: ID) => `/api/warehouses/${id}`,
    DELETE: (id: ID) => `/api/warehouses/${id}`,
    STOCK: (id: ID) => `/api/warehouses/${id}/stock`,
  },

  // 倉庫在庫管理
  WAREHOUSE_STOCK: {
    LIST: '/api/warehouse-stock',
    GET: (id: ID) => `/api/warehouse-stock/${id}`,
    UPDATE: (id: ID) => `/api/warehouse-stock/${id}`,
    TRANSFER: '/api/warehouse-stock/transfer',
    BULK_UPDATE: '/api/warehouse-stock/bulk-update',
  },

  // 入庫管理
  INBOUND_SHIPMENTS: {
    LIST: '/api/inbound-shipments',
    GET: (id: ID) => `/api/inbound-shipments/${id}`,
    CREATE: '/api/inbound-shipments',
    UPDATE: (id: ID) => `/api/inbound-shipments/${id}`,
    RECEIVE: (id: ID) => `/api/inbound-shipments/${id}/receive`,
  },

  // 通知管理
  NOTIFICATIONS: {
    TEMPLATES: '/api/notifications/templates',
    GET_TEMPLATE: (id: ID) => `/api/notifications/templates/${id}`,
    CREATE_TEMPLATE: '/api/notifications/templates',
    UPDATE_TEMPLATE: (id: ID) => `/api/notifications/templates/${id}`,
    DELETE_TEMPLATE: (id: ID) => `/api/notifications/templates/${id}`,
    QUEUE: '/api/notifications/queue',
    SEND: '/api/notifications/send',
    PREFERENCES: '/api/notifications/preferences',
    UPDATE_PREFERENCES: '/api/notifications/preferences',
  },

  // サポートチケット管理
  SUPPORT_TICKETS: {
    LIST: '/api/support-tickets',
    GET: (id: ID) => `/api/support-tickets/${id}`,
    CREATE: '/api/support-tickets',
    UPDATE: (id: ID) => `/api/support-tickets/${id}`,
    CLOSE: (id: ID) => `/api/support-tickets/${id}/close`,
    ASSIGN: (id: ID) => `/api/support-tickets/${id}/assign`,
  },

  // 顧客ノート管理
  CUSTOMER_NOTES: {
    LIST: (buyerId: ID) => `/api/buyers/${buyerId}/notes`,
    CREATE: (buyerId: ID) => `/api/buyers/${buyerId}/notes`,
    UPDATE: (buyerId: ID, noteId: ID) => `/api/buyers/${buyerId}/notes/${noteId}`,
    DELETE: (buyerId: ID, noteId: ID) => `/api/buyers/${buyerId}/notes/${noteId}`,
  },

  // 監査ログ
  AUDIT_LOGS: {
    LIST: '/api/audit-logs',
    GET: (id: ID) => `/api/audit-logs/${id}`,
    SEARCH: '/api/audit-logs/search',
    EXPORT: '/api/audit-logs/export',
  },

  // セキュリティ
  SECURITY: {
    LOGIN_ATTEMPTS: '/api/security/login-attempts',
    EVENTS: '/api/security/events',
    RESOLVE_EVENT: (id: ID) => `/api/security/events/${id}/resolve`,
  },

  // 翻訳管理
  TRANSLATIONS: {
    KEYS: '/api/translations/keys',
    GET_KEY: (id: ID) => `/api/translations/keys/${id}`,
    CREATE_KEY: '/api/translations/keys',
    UPDATE_KEY: (id: ID) => `/api/translations/keys/${id}`,
    DELETE_KEY: (id: ID) => `/api/translations/keys/${id}`,
    AUTO_TRANSLATE: '/api/translations/auto-translate',
    LOGS: '/api/translations/logs',
  },

  // 為替レート管理
  EXCHANGE_RATES: {
    LIST: '/api/exchange-rates',
    GET: (id: ID) => `/api/exchange-rates/${id}`,
    UPDATE: '/api/exchange-rates/update',
    CONVERT: '/api/exchange-rates/convert',
    HISTORY: '/api/exchange-rates/history',
  },

  // Apify連携管理
  APIFY: {
    ACTORS: '/api/apify/actors',
    GET_ACTOR: (id: string) => `/api/apify/actors/${id}`,
    RUN_ACTOR: (id: string) => `/api/apify/actors/${id}/run`,
    GET_RUN: (actorId: string, runId: string) => `/api/apify/actors/${actorId}/runs/${runId}`,
    GET_DATASET: (datasetId: string) => `/api/apify/datasets/${datasetId}`,
    DOWNLOAD_DATASET: (datasetId: string) => `/api/apify/datasets/${datasetId}/download`,
    WEBHOOK: '/api/apify/webhook',
    SCHEDULES: '/api/apify/schedules',
    CREATE_SCHEDULE: '/api/apify/schedules',
    UPDATE_SCHEDULE: (id: ID) => `/api/apify/schedules/${id}`,
    DELETE_SCHEDULE: (id: ID) => `/api/apify/schedules/${id}`,
    STATUS: '/api/apify/status',
  },

  // オークション管理
  AUCTIONS: {
    LIST: '/api/auctions',
    GET: (id: ID) => `/api/auctions/${id}`,
    CREATE: '/api/auctions',
    UPDATE: (id: ID) => `/api/auctions/${id}`,
    DELETE: (id: ID) => `/api/auctions/${id}`,
    PLACE_BID: (id: ID) => `/api/auctions/${id}/bid`,
    BID_HISTORY: (id: ID) => `/api/auctions/${id}/bids`,
    STATUS: (id: ID) => `/api/auctions/${id}/status`,
    WATCH: (id: ID) => `/api/auctions/${id}/watch`,
    UNWATCH: (id: ID) => `/api/auctions/${id}/unwatch`,
    EXTEND: (id: ID) => `/api/auctions/${id}/extend`,
    END_EARLY: (id: ID) => `/api/auctions/${id}/end`,
    ACTIVE: '/api/auctions/active',
    ENDING_SOON: '/api/auctions/ending-soon',
    MY_BIDS: '/api/auctions/my-bids',
    MY_WATCHING: '/api/auctions/my-watching',
  },


  // 代理購入仕入確認
  PROXY_CONFIRMATIONS: {
    LIST: '/api/proxy-confirmations',
    GET: (id: ID) => `/api/proxy-confirmations/${id}`,
    CREATE: '/api/proxy-confirmations',
    UPDATE: (id: ID) => `/api/proxy-confirmations/${id}`,
    APPROVE: (id: ID) => `/api/proxy-confirmations/${id}/approve`,
    REJECT: (id: ID) => `/api/proxy-confirmations/${id}/reject`,
    PHOTOS: (id: ID) => `/api/proxy-confirmations/${id}/photos`,
    UPLOAD_PHOTO: (id: ID) => `/api/proxy-confirmations/${id}/photos`,
    DELETE_PHOTO: (id: ID, photoId: ID) => `/api/proxy-confirmations/${id}/photos/${photoId}`,
    PENDING: '/api/proxy-confirmations/pending',
  },

  // 代理商品登録
  PROXY_REGISTRATIONS: {
    LIST: '/api/proxy-registrations',
    GET: (id: ID) => `/api/proxy-registrations/${id}`,
    CREATE: '/api/proxy-registrations',
    UPDATE: (id: ID) => `/api/proxy-registrations/${id}`,
    SUBMIT: (id: ID) => `/api/proxy-registrations/${id}/submit`,
    APPROVE: (id: ID) => `/api/proxy-registrations/${id}/approve`,
    REJECT: (id: ID) => `/api/proxy-registrations/${id}/reject`,
    PUBLISH: (id: ID) => `/api/proxy-registrations/${id}/publish`,
    PHOTOS: (id: ID) => `/api/proxy-registrations/${id}/photos`,
    UPLOAD_PHOTO: (id: ID) => `/api/proxy-registrations/${id}/photos`,
    REORDER_PHOTOS: (id: ID) => `/api/proxy-registrations/${id}/photos/reorder`,
    PENDING_APPROVAL: '/api/proxy-registrations/pending-approval',
  },

  // 詳細見積もりシミュレーション
  ESTIMATE_SIMULATION: {
    REQUEST: '/api/estimate-simulation/request',
    GET_REQUEST: (id: ID) => `/api/estimate-simulation/requests/${id}`,
    CALCULATE_DETAILED: '/api/estimate-simulation/calculate-detailed',
    WEIGHT_CALCULATOR: '/api/estimate-simulation/weight-calculator',
    DIMENSION_CALCULATOR: '/api/estimate-simulation/dimension-calculator',
    SHIPPING_OPTIONS: '/api/estimate-simulation/shipping-options',
    MARKET_ANALYSIS: '/api/estimate-simulation/market-analysis',
    BULK_CALCULATE: '/api/estimate-simulation/bulk-calculate',
    SAVE_TEMPLATE: '/api/estimate-simulation/templates',
    GET_TEMPLATE: (id: ID) => `/api/estimate-simulation/templates/${id}`,
  },

  // 通貨設定
  CURRENCY_SETTINGS: {
    LIST: '/api/currency-settings',
    GET: (id: ID) => `/api/currency-settings/${id}`,
    UPDATE: (id: ID) => `/api/currency-settings/${id}`,
  },

  // 見積もり管理
  ESTIMATES: {
    LIST: '/api/estimates',
    GET: (id: ID) => `/api/estimates/${id}`,
    CREATE: '/api/estimates',
    UPDATE: (id: ID) => `/api/estimates/${id}`,
    DELETE: (id: ID) => `/api/estimates/${id}`,
    CALCULATE: '/api/estimates/calculate',
    APPROVE: (id: ID) => `/api/estimates/${id}/approve`,
    REJECT: (id: ID) => `/api/estimates/${id}/reject`,
    CANCEL: (id: ID) => `/api/estimates/${id}/cancel`,
    NEGOTIATIONS: (id: ID) => `/api/estimates/${id}/negotiations`,
    ADD_NEGOTIATION: (id: ID) => `/api/estimates/${id}/negotiations`,
    ATTACHMENTS: (id: ID) => `/api/estimates/${id}/attachments`,
    UPLOAD_ATTACHMENT: (id: ID) => `/api/estimates/${id}/attachments`,
    DELETE_ATTACHMENT: (id: ID, attachmentId: ID) => `/api/estimates/${id}/attachments/${attachmentId}`,
    BY_USER: (userId: ID) => `/api/estimates/user/${userId}`,
    BY_STATUS: (status: string) => `/api/estimates/status/${status}`,
    EXPORT: '/api/estimates/export',
  },

  // FAQ管理
  FAQ: {
    LIST: '/api/faq',
    GET: (id: ID) => `/api/faq/${id}`,
    CREATE: '/api/faq',
    UPDATE: (id: ID) => `/api/faq/${id}`,
    DELETE: (id: ID) => `/api/faq/${id}`,
    BY_CATEGORY: (category: string) => `/api/faq/category/${category}`,
    SEARCH: '/api/faq/search',
    VOTE: (id: ID) => `/api/faq/${id}/vote`,
    POPULAR: '/api/faq/popular',
    RECENT: '/api/faq/recent',
  },

  // ヘルプ記事管理
  HELP_ARTICLES: {
    LIST: '/api/help-articles',
    GET: (id: ID) => `/api/help-articles/${id}`,
    CREATE: '/api/help-articles',
    UPDATE: (id: ID) => `/api/help-articles/${id}`,
    DELETE: (id: ID) => `/api/help-articles/${id}`,
    PUBLISH: (id: ID) => `/api/help-articles/${id}/publish`,
    VOTE: (id: ID) => `/api/help-articles/${id}/vote`,
    BY_CATEGORY: (category: string) => `/api/help-articles/category/${category}`,
    SEARCH: '/api/help-articles/search',
    POPULAR: '/api/help-articles/popular',
    RECENT: '/api/help-articles/recent',
  },

  // ヘルプ・サポート
  HELP: {
    CATEGORIES: '/api/help/categories',
    SEARCH: '/api/help/search',
    CONTACT: '/api/help/contact',
    POPULAR: '/api/help/popular',
    RECENT: '/api/help/recent',
  },

  // Webhook管理
  WEBHOOKS: {
    ENDPOINTS: '/api/webhooks/endpoints',
    GET_ENDPOINT: (id: ID) => `/api/webhooks/endpoints/${id}`,
    CREATE_ENDPOINT: '/api/webhooks/endpoints',
    UPDATE_ENDPOINT: (id: ID) => `/api/webhooks/endpoints/${id}`,
    DELETE_ENDPOINT: (id: ID) => `/api/webhooks/endpoints/${id}`,
    TEST_ENDPOINT: (id: ID) => `/api/webhooks/endpoints/${id}/test`,
    EVENTS: '/api/webhooks/events',
    RETRY_EVENT: (id: ID) => `/api/webhooks/events/${id}/retry`,
  },

  // システム設定
  SYSTEM_SETTINGS: {
    LIST: '/api/system-settings',
    GET: (key: string) => `/api/system-settings/${key}`,
    UPDATE: (key: string) => `/api/system-settings/${key}`,
    BULK_UPDATE: '/api/system-settings/bulk-update',
  },
} as const;

export interface ApifyActorConfig {
  actorId: ID;
  runId?: string;
  timeout: number; // seconds
  memoryMb: number;
  maxItems?: number;
  datasetId?: string;
  webhookUrl?: string;
  build?: string;
  waitForFinish?: boolean;
}

export enum ApifyRunStatus {
  READY = 'ready',
  RUNNING = 'running',
  SUCCEEDED = 'succeeded',
  FAILED = 'failed',
  TIMING_OUT = 'timing_out',
  TIMED_OUT = 'timed_out',
  ABORTING = 'aborting',
  ABORTED = 'aborted'
}

export interface ApifyRunResult {
  id: ID;
  actId: ID;
  userId: ID;
  startedAt: Date;
  finishedAt?: Date;
  status: ApifyRunStatus;
  statusMessage?: string;
  isStatusMessageTerminal: boolean;
  meta: {
    origin: string;
    clientIp: string;
    userAgent: string;
  };
  stats: {
    inputBodyLen: number;
    restartCount: number;
    resurrectCount: number;
    memAvgBytes: number;
    memMaxBytes: number;
    memCurrentBytes: number;
    cpuAvgUsage: number;
    cpuMaxUsage: number;
    cpuCurrentUsage: number;
    netRxBytes: number;
    netTxBytes: number;
    durationMillis: number;
    runTimeSecs: number;
    metamorph: number;
    computeUnits: number;
  };
  options: {
    build: string;
    memoryMbytes: number;
    timeoutSecs: number;
  };
  buildId: ID;
  exitCode?: number;
  defaultKeyValueStoreId: ID;
  defaultDatasetId: ID;
  defaultRequestQueueId: ID;
  containerUrl?: string;
}

export interface ApifyWebhookPayload {
  eventType: 'actor.run.succeeded' | 'actor.run.failed' | 'actor.run.aborted' | 'actor.run.timed_out';
  eventData: {
    actorId: ID;
    actorRunId: ID;
    actorTaskId?: string;
  };
  resource: {
    id: ID;
    actId: ID;
    userId: ID;
    status: ApifyRunStatus;
    startedAt: string;
    finishedAt?: string;
    buildId: ID;
    exitCode?: number;
    defaultDatasetId: ID;
    defaultKeyValueStoreId: ID;
    defaultRequestQueueId: ID;
    stats: {
      netRxBytes: number;
      netTxBytes: number;
      cpuAvgUsage: number;
      cpuMaxUsage: number;
      cpuCurrentUsage: number;
      memAvgBytes: number;
      memMaxBytes: number;
      memCurrentBytes: number;
      durationMillis: number;
      runTimeSecs: number;
      metamorph: number;
      computeUnits: number;
    };
  };
}

export interface ApifyDataset {
  id: ID;
  name?: string;
  userId: ID;
  createdAt: Date;
  modifiedAt: Date;
  accessedAt: Date;
  itemCount: number;
  cleanItemCount: number;
  actId?: string;
  actRunId?: string;
}

export interface ApifyRunInput {
  targetUrl?: string;
  searchKeyword?: string;
  maxItems?: number;
  categoryIds?: string[];
  priceRange?: {
    min: number;
    max: number;
  };
  condition?: string[];
  location?: string[];
  customOptions?: Record<string, unknown>;
}

export interface ApifyScrapedItem {
  id: ID;
  title: string;
  description?: string;
  price: number;
  currency: string;
  images: string[];
  condition: string;
  seller: {
    name: string;
    id?: string;
    rating?: number;
    reviewCount?: number;
  };
  location?: string;
  category?: string;
  subcategory?: string;
  url: string;
  availability: 'available' | 'sold' | 'unavailable';
  scrapedAt: Date;
  platform: 'mercari' | 'yauction';
  externalId: ID;
  shipping?: {
    cost?: number;
    method?: string;
    location?: string;
  };
  attributes?: Record<string, unknown>;
}

// ===== 代理購入・仕入確認の詳細型定義 =====
export interface ProxyItemPhoto extends Timestamps {
  id: ID;
  proxyOrderId: ID;
  photoUrl: string;
  thumbnailUrl?: string;
  photoType: 'received_item' | 'packaging' | 'damage' | 'detail' | 'other';
  description?: string;
  takenAt: Date;
  sortOrder: number;
  isMain: boolean;
  metadata?: {
    cameraInfo?: string;
    location?: string;
    fileSize?: number;
    dimensions?: {
      width: number;
      height: number;
    };
  };
}

export interface ProxyConfirmation extends Timestamps {
  id: ID;
  proxyOrderId: ID;
  confirmationType: 'item_received' | 'condition_check' | 'price_verification' | 'shipping_ready';
  status: 'pending' | 'approved' | 'rejected' | 'requires_action';
  adminId?: ID;
  buyerId: ID;
  photos: ProxyItemPhoto[];
  originalPrice: number;
  actualPrice?: number;
  priceDifference?: number;
  conditionNotes?: string;
  buyerMessage?: string;
  adminResponse?: string;
  approvalDeadline: Date;
  approvedAt?: Date;
  rejectedAt?: Date;
  rejectionReason?: string;
  requiresBuyerApproval: boolean;
  buyerApprovalStatus?: 'pending' | 'approved' | 'rejected';
  buyerApprovedAt?: Date;
  notifications?: {
    emailSent: boolean;
    smsSent: boolean;
    inAppSent: boolean;
    sentAt?: Date;
  };
}

export interface ProxyItemRegistration extends Timestamps {
  id: ID;
  proxyOrderId: ID;
  productBasicInfo: {
    name: MultiLanguageText;
    description: MultiLanguageText;
    category: string;
    subcategory?: string;
    brand?: string;
    model?: string;
    color?: string;
    size?: string;
    condition: 'new' | 'like_new' | 'good' | 'fair' | 'poor';
    conditionDescription?: string;
  };
  photos: ProxyItemPhoto[]; // 最大10枚
  dimensions?: {
    width: number;
    height: number;
    depth: number;
    weight: number;
    unit: 'cm' | 'inch';
    weightUnit: 'kg' | 'g' | 'lb' | 'oz';
  };
  originalListing: {
    platform: 'mercari' | 'yauction';
    listingId: ID;
    originalUrl: string;
    originalPrice: number;
    originalCurrency: Currency;
    originalTitle: string;
    originalDescription?: string;
    originalImages: string[];
    listedAt: Date;
  };
  registrationStatus: 'draft' | 'submitted' | 'approved' | 'rejected' | 'published';
  registeredBy: ID;
  approvedBy?: ID;
  rejectionReason?: string;
  notes?: string;
  internalNotes?: string;
  tagsManual: string[];
  tagsAutoGenerated: string[];
  seoKeywords?: string[];
  estimatedValue?: {
    min: number;
    max: number;
    currency: Currency;
    basedOn: 'market_analysis' | 'manual_estimate' | 'historical_data';
  };
}

// ===== オークション管理関連の型定義 =====
export interface AuctionItem extends Timestamps {
  id: ID;
  productId?: ID;
  externalProductId?: ID;
  auctionTitle: MultiLanguageText;
  description: MultiLanguageText;
  images: string[];
  startingPrice: number;
  currentPrice: number;
  buyNowPrice?: number;
  currency: Currency;
  startTime: Date;
  endTime: Date;
  extendOnBid: boolean; // 入札時の延長
  extensionMinutes?: number;
  status: 'upcoming' | 'active' | 'ended' | 'cancelled' | 'sold';
  sellerId: ID;
  categoryId: ID;
  condition: 'new' | 'like_new' | 'good' | 'fair' | 'poor';
  shippingInfo: {
    domesticShipping: number;
    internationalShipping: number;
    freeShippingThreshold?: number;
    estimatedDays: number;
  };
  bidHistory: BidHistory[];
  watchCount: number;
  viewCount: number;
  isReserved: boolean; // 最低落札価格あり
  reservePrice?: number;
  tags: string[];
  specifications?: Record<string, string>;
  auctionNumber: string;
}

export interface BidHistory extends Timestamps {
  id: ID;
  auctionId: ID;
  bidderId: ID;
  bidderName?: string; // 匿名化された表示名
  bidAmount: number;
  maxBidAmount: number; // 自動入札の最大額
  isAutoBid: boolean;
  bidType: 'manual' | 'auto' | 'proxy';
  bidTime: Date;
  ipAddress?: string;
  userAgent?: string;
  isWinning: boolean;
  isValid: boolean;
  invalidReason?: string;
  retractedAt?: Date;
  retractionReason?: string;
}

export interface BidRequest {
  auctionId: ID;
  bidAmount: number;
  maxBidAmount?: number; // 自動入札の場合
  isAutoBid?: boolean;
  proxyBidEnabled?: boolean;
}

export interface AuctionStatus {
  auctionId: ID;
  currentPrice: number;
  bidCount: number;
  highestBidderId?: ID;
  timeRemaining: number; // seconds
  isExtended: boolean;
  extensionCount: number;
  lastBidAt?: Date;
  nextMinimumBid: number;
  reserveMet: boolean;
  watchlistCount: number;
  viewCount: number;
}

// ===== 見積もりシミュレーション詳細型定義 =====
export interface EstimateRequestDetailed extends Timestamps {
  id: ID;
  userId: ID;
  requestType: 'url_based' | 'description_based' | 'photo_based';
  // URL ベースの場合
  productUrls?: string[];
  // 説明ベースの場合
  productDescription?: {
    name: string;
    description: string;
    estimatedPrice?: number;
    currency?: Currency;
    category?: string;
    brand?: string;
    model?: string;
    specifications?: Record<string, string>;
  };
  // 写真ベースの場合
  productPhotos?: string[];
  // 共通項目
  quantity: number;
  dimensions?: {
    width?: number;
    height?: number;
    depth?: number;
    weight?: number;
    unit: 'cm' | 'inch';
    weightUnit: 'kg' | 'g' | 'lb' | 'oz';
    isEstimated: boolean;
  };
  shippingPreferences: {
    speed: 'economy' | 'standard' | 'express' | 'overnight';
    insurance: boolean;
    tracking: boolean;
    signature: boolean;
    destination: Address;
  };
  notes?: string;
  urgency: 'low' | 'medium' | 'high';
  budgetRange?: {
    min: number;
    max: number;
    currency: Currency;
  };
  status: 'submitted' | 'analyzing' | 'ready' | 'expired';
  expiresAt: Date;
}

export interface EstimateResultDetailed {
  requestId: ID;
  estimatedAt: Date;
  validUntil: Date;
  // 商品価格
  itemPricing: {
    basePrice: number;
    priceRange: {
      min: number;
      max: number;
    };
    currency: Currency;
    confidence: 'low' | 'medium' | 'high';
    basedOn: 'market_data' | 'manual_estimate' | 'ai_analysis';
    similarProducts?: {
      id: ID;
      name: string;
      price: number;
      url?: string;
    }[];
  };
  // 配送料金
  shippingOptions: ShippingOptionDetailed[];
  // 手数料
  fees: {
    proxyServiceFee: {
      amount: number;
      rate: number; // percentage
      minimum?: number;
      maximum?: number;
    };
    paymentProcessingFee: {
      amount: number;
      rate: number;
    };
    customsFee?: {
      estimatedAmount: number;
      description: string;
      disclaimer: string;
    };
    inspectionFee?: {
      amount: number;
      description: string;
    };
    storageFee?: {
      dailyRate: number;
      freeStorageDays: number;
    };
  };
  // 総額
  totalEstimate: {
    subtotal: number;
    totalFees: number;
    totalShipping: number;
    grandTotal: number;
    currency: Currency;
    breakdown: Array<{
      item: string;
      amount: number;
      description?: string;
    }>;
  };
  // タイムライン
  estimatedTimeline: {
    orderProcessing: number; // days
    domesticShipping: number; // days
    internationalShipping: number; // days
    customsClearance: number; // days
    finalDelivery: number; // days
    totalDays: number;
    estimatedDeliveryDate: Date;
  };
  // リスク・注意事項
  risks: Array<{
    type: 'availability' | 'price_change' | 'shipping_delay' | 'customs' | 'damage' | 'other';
    description: string;
    likelihood: 'low' | 'medium' | 'high';
    impact: 'minor' | 'moderate' | 'major';
  }>;
  // 追加情報
  additionalInfo: {
    exchangeRates: Record<Currency, number>;
    marketTrends?: string;
    seasonalFactors?: string;
    recommendations?: string[];
  };
}

export interface ShippingOptionDetailed {
  id: ID;
  name: string;
  carrierName: string;
  serviceType: 'economy' | 'standard' | 'express' | 'overnight';
  estimatedDays: {
    min: number;
    max: number;
    typical: number;
  };
  cost: {
    base: number;
    additional?: number; // per kg or per item
    currency: Currency;
    fuelSurcharge?: number;
    remoteFee?: number;
  };
  features: {
    tracking: boolean;
    insurance: boolean;
    signatureRequired: boolean;
    customsHandling: boolean;
    doorToDoor: boolean;
  };
  restrictions: {
    maxWeight?: number;
    maxDimensions?: {
      length: number;
      width: number;
      height: number;
    };
    prohibitedItems?: string[];
  };
  reliability: {
    onTimeRate: number; // percentage
    lossRate: number; // percentage
    damageRate: number; // percentage
  };
  available: boolean;
  unavailableReason?: string;
}

export interface WeightCalculation {
  actualWeight?: number;
  volumetricWeight?: number;
  chargeableWeight: number;
  unit: 'kg' | 'lb';
  dimensions: {
    length: number;
    width: number;
    height: number;
    unit: 'cm' | 'inch';
  };
  volumetricDivisor: number; // e.g., 5000 for DHL
  calculation: {
    formula: string;
    steps: string[];
  };
}

export interface DimensionCalculation {
  original: {
    length: number;
    width: number;
    height: number;
    unit: 'cm' | 'inch';
  };
  packagedDimensions: {
    length: number;
    width: number;
    height: number;
    unit: 'cm' | 'inch';
    packagingType: string;
    additionalPadding: number;
  };
  oversizeCharges?: {
    lengthCharge: number;
    girthCharge: number;
    description: string;
  };
}

// ===== 外部サイト管理のApify対応型定義 =====
export interface ExternalSiteConfigApify extends ExternalSite {
  apifySettings: {
    actorId: ID;
    runInput: ApifyRunInput;
    scheduledRuns: {
      enabled: boolean;
      frequency: 'hourly' | 'daily' | 'weekly';
      time?: string;
      dayOfWeek?: number;
      lastRunId?: string;
      nextRunAt?: Date;
    };
    webhookSettings: {
      enabled: boolean;
      url: string;
      events: ('actor.run.succeeded' | 'actor.run.failed' | 'actor.run.aborted' | 'actor.run.timed_out')[];
      secret?: string;
    };
    dataProcessing: {
      autoImport: boolean;
      priceRuleId?: ID;
      categoryMappingRules: CategoryMapping[];
      qualityFilters: {
        minRating?: number;
        minImages?: number;
        excludeConditions?: string[];
        priceRange?: {
          min: number;
          max: number;
        };
      };
    };
    errorHandling: {
      maxRetries: number;
      retryDelay: number; // seconds
      notificationThreshold: number; // consecutive failures
      emailAlerts: boolean;
      slackAlerts: boolean;
    };
  };
}

export interface ScrapingScheduleApify extends Timestamps {
  id: ID;
  externalSiteId: ID;
  actorId: ID;
  name: string;
  description?: string;
  frequency: 'hourly' | 'daily' | 'weekly' | 'monthly' | 'manual';
  scheduleConfig: {
    time?: string; // HH:MM format
    dayOfWeek?: number; // 0-6 (Sunday=0)
    dayOfMonth?: number; // 1-31
    timezone: string;
  };
  runInput: ApifyRunInput;
  lastRunId?: string;
  lastRunAt?: Date;
  lastRunStatus?: ApifyRunStatus;
  nextRunAt?: Date;
  isActive: boolean;
  failureCount: number;
  consecutiveFailures: number;
  maxRetries: number;
  metadata?: {
    createdBy: ID;
    tags?: string[];
    priority: 'low' | 'medium' | 'high';
  };
}

export interface DataSyncStatusApify extends Timestamps {
  id: ID;
  externalSiteId: ID;
  runId: ID;
  actorId: ID;
  syncType: 'full' | 'incremental' | 'manual' | 'scheduled';
  status: ApifyRunStatus;
  progress: {
    totalItems: number;
    processedItems: number;
    successfulItems: number;
    failedItems: number;
    skippedItems: number;
    percentage: number;
  };
  metrics: {
    startTime: Date;
    endTime?: Date;
    duration?: number; // seconds
    itemsPerMinute?: number;
    datasetId?: string;
    datasetSize?: number; // bytes
    memoryUsage?: number; // bytes
    computeUnits?: number;
  };
  errors: Array<{
    type: 'validation_error' | 'import_error' | 'network_error' | 'parsing_error';
    message: string;
    item?: Record<string, unknown>;
    count: number;
  }>;
  results: {
    newProducts: number;
    updatedProducts: number;
    unchangedProducts: number;
    deletedProducts: number;
    categoryMappings: number;
    priceUpdates: number;
  };
  notifications: {
    emailSent: boolean;
    slackSent: boolean;
    webhookSent: boolean;
    sentAt?: Date;
  };
  retryInfo?: {
    attempt: number;
    maxAttempts: number;
    nextRetryAt?: Date;
    retryReason: string;
  };
}

// ===== 代理購入関連の型定義 =====
export enum ProxyPurchaseStatus {
  NEW = 'new',
  PURCHASING = 'purchasing',
  PURCHASED = 'purchased',
  SHIPPING_DOMESTIC = 'shipping_domestic',
  ARRIVED_WAREHOUSE = 'arrived_warehouse',
  REPACKAGING = 'repackaging',
  SHIPPING_INTERNATIONAL = 'shipping_international',
  DELIVERED = 'delivered',
  CANCELLED = 'cancelled',
}

// 商品状態Enum（productsテーブルと統一）
export enum ProductCondition {
  NEW = 'new',               // 新品
  LIKE_NEW = 'like_new',     // 未使用に近い
  EXCELLENT = 'excellent',   // 目立った傷や汚れなし
  GOOD = 'good',             // やや傷や汚れあり
  ACCEPTABLE = 'acceptable', // 傷や汚れあり
  DAMAGED = 'damaged'        // 全体的に状態が悪い
}

export interface ExternalProduct extends Timestamps {
  id: ID;
  sourcePlatform: 'mercari' | 'yauction';
  externalId: ID;
  productCode: string; // 外部商品コード（URL識別用）
  title: MultiLanguageText;
  description: MultiLanguageText;
  price: number;
  currency: Currency;
  images: string[];
  categoryId: ID;
  condition: ProductCondition;
  seller: string;
  sellerName?: MultiLanguageText;
  location: MultiLanguageText;
  availability: 'available' | 'sold' | 'unavailable';
  lastSynced: Date;
  url: string;
  averageRating?: number;          // 平均評価
  reviewCount?: number;            // レビュー件数
  favoriteCount?: number;          // お気に入り件数
  sellerRating: number;            // セラー評価
  sellerRatingCount?: number;      // セラー評価件数
  likesCount?: number;             // いいね数（メルカリ）/ウォッチ数（ヤフオク）
  currentStock: number;            // 在庫数（productsテーブルと統一）
  shippingCost?: number;           // 配送料
  estimatedDeliveryDays?: number;  // 配送予定日数
}

// メルカリ商品状態（廃止：ProductCondition統一）
// export enum MercariCondition {
//   NEW_UNUSED = 'new_unused', // 新品、未使用
//   NEARLY_UNUSED = 'nearly_unused', // 未使用に近い
//   NO_VISIBLE_DAMAGE = 'no_visible_damage', // 目立った傷や汚れなし
//   SLIGHTLY_DAMAGED = 'slightly_damaged', // やや傷や汚れあり
//   DAMAGED = 'damaged', // 傷や汚れあり
//   HEAVILY_DAMAGED = 'heavily_damaged', // 全体的に状態が悪い
// }

// メルカリ専用商品型
export interface MercariProduct extends ExternalProduct {
  sourcePlatform: 'mercari';
  productCode: string; // メルカリ商品コード（URL識別用）
  // condition: ProductConditionを継承（ExternalProductから）
  shippingPayer: 'seller' | 'buyer';
  shippingMethod: MultiLanguageText;
  shippingOrigin: MultiLanguageText;
  daysToShip: MultiLanguageText;
  likes: number;
  views: number;
  soldOut: boolean;
  negotiable: boolean;
  originCountry?: MultiLanguageText;
  material?: string;
  [key: string]: unknown;
}

// ヤフオク入札戦略
export enum BidStrategy {
  MANUAL = 'manual', // 手動入札
  SNIPE = 'snipe', // スナイプ入札
  INCREMENTAL = 'incremental', // 段階的入札
  IMMEDIATE = 'immediate', // 即決
}

// ヤフオク商品型
export interface YahooAuctionProduct extends ExternalProduct {
  sourcePlatform: 'yauction';
  productCode: string; // ヤフオク商品コード（URL識別用）
  auctionEndTime: Date;
  currentBid: number;
  buyNowPrice?: number;
  bidCount: number;
  watchCount: number;
  sellerId: ID;
  sellerRating: number;
  sellerRatingCount: number;
  shippingCost: number;
  returnable: boolean;
  bidStrategy?: BidStrategy;
  [key: string]: unknown;
}

// 輸入禁止品目カテゴリー
export enum ProhibitedItemCategory {
  WEAPONS_FIREARMS = 'weapons_firearms',     // 武器・火器類
  NARCOTICS_DRUGS = 'narcotics_drugs',       // 麻薬・薬物類
  EXPLOSIVES = 'explosives',                 // 爆発物・危険物
  COUNTERFEIT = 'counterfeit',               // 偽造品・模倣品
  ADULT_CONTENT = 'adult_content',           // 成人向けコンテンツ
  PROTECTED_SPECIES = 'protected_species',   // 保護動植物
  FOOD_MEDICAL = 'food_medical',             // 食品・医薬品（未承認）
  CULTURAL_ASSETS = 'cultural_assets',       // 文化財
}

export interface ProhibitedItem {
  id: ID;
  category: ProhibitedItemCategory;
  name: string;
  description: string;
  keywords: string[];
  countrySpecific?: string[]; // 特定の国のみ禁止
  active: boolean;
}

// 価格表示形式
export interface PriceDisplay {
  primaryAmount: number;
  primaryCurrency: Currency;
  secondaryAmount: number; // USD換算額
  secondaryCurrency: 'USD';
  formattedPrimary: string; // "¥12,000"
  formattedSecondary: string; // "$82.50"
  combinedDisplay: string; // "¥12,000 ($82.50)"
}

// オークション入札型
export interface AuctionBid extends Timestamps {
  id: ID;
  auctionId: ID;
  userId: ID;
  maxBidAmount: number;
  currentBidAmount: number;
  bidType: BidStrategy;
  status: 'active' | 'outbid' | 'won' | 'lost' | 'cancelled';
  bidHistory: {
    amount: number;
    timestamp: Date;
  }[];
}

// オークション決済（2段階）
export interface AuctionPayment extends Timestamps {
  id: ID;
  bidId: ID;
  orderId: ID;
  firstPayment: {
    amount: number;
    status: PaymentStatus;
    paidAt?: Date;
    transactionId?: string;
  };
  secondPayment: {
    amount: number;
    status: PaymentStatus;
    paidAt?: Date;
    transactionId?: string;
  };
  totalAmount: number;
  refundAmount?: number;
  refundReason?: string;
}

// 代理購入商品確認型
export interface ProxyItemConfirmation extends Timestamps {
  id: ID;
  proxyOrderId: ID;
  userId: ID;
  status: 'pending' | 'approved' | 'rejected';
  photos: ProxyItemPhoto[];
  comment?: string;
  confirmedAt?: Date;
  rejectionReason?: string;
}

// 配送スナップショット型（決済時の配送情報・支払情報キャプチャ）
export interface ShippingSnapshot {
  buyer: {
    name: string;
    email: string;
    phone: string;
  };
  shippingAddress: {
    recipientName: string;
    country: string;
    postalCode: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    stateProvince?: string;
    phone: string;
    personalCustomsCode?: string; // 韓国個人通関固有符号 (PCCC)
  };
  shippingMethod: {
    id: string;
    name: string;
    carrier: string;
    estimatedDays: string;
  };
  paymentMethod: {
    id: string;
    type: string;
    cardLast4?: string;
    cardBrand?: string;
  };
}

export interface ProxyPurchaseOrder extends Timestamps {
  id: ID;
  orderNumber: string;
  userId: ID;
  externalProductId: ID;
  status: ProxyPurchaseStatus;
  requestedQuantity: number;
  productPrice: number;
  domesticShippingFee: number;
  agentFee: number;
  inspectionFee?: number; // 検査手数料
  internationalShipping: number;
  shippingInsuranceFee?: number; // 配送保険料
  totalAmount: number;
  currency: Currency;
  shippingAddress: Address;
  shippingSnapshot?: Record<string, any>; // 配送スナップショット（JSON）
  paymentId?: string; // 決済ID
  notes?: string;
  estimatedDelivery?: Date;
  timeline: ProxyPurchaseTimelineEntry[];
  paymentMethod: PaymentMethod;
  paymentStatus: PaymentStatus;
  proxyPurchaseNumber: string; // 代理購入番号
  domesticShipping: number; // 国内配送料
  customsFee?: number; // 関税見積もり
  taxFee?: number; // 消費税見積もり
  trackingNumberDomestic?: string; // 国内追跡番号
  trackingNumberInternational?: string; // 国際追跡番号
  productSnapshot?: Record<string, any>; // 商品スナップショット
  estimatedDeliveryDate?: string; // 配送予定日
  actualDeliveryDate?: string; // 実際の配送日
  exchangeRate?: number; // 為替レート
  cancellationReason?: string; // キャンセル理由
  cancelledAt?: string; // キャンセル日時
  refundAmount?: number; // 返金額
  refundStatus?: string; // 返金ステータス
}

export interface ProxyPurchaseTimelineEntry {
  id: ID;
  timestamp: Date;
  status: ProxyPurchaseStatus;
  description: string;
  isVisible: boolean;
  adminNotes?: string;
}

export interface ProxyPurchaseCalculation {
  productPrice: number;
  domesticShippingFee: number;
  agentFeeRate: number;
  agentFee: number;
  inspectionFee: number;
  internationalShipping: number;
  totalAmount: number;
  currency: Currency;
  exchangeRate?: number;
}

// ===== マーケティング関連の型定義 =====
export enum CampaignType {
  BANNER = 'banner',
  EMAIL = 'email',
  PUSH = 'push',
  COUPON = 'coupon',
  SALE = 'sale',
}

export enum CampaignStatus {
  DRAFT = 'draft',
  SCHEDULED = 'scheduled',
  ACTIVE = 'active',
  PAUSED = 'paused',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export interface MarketingCampaign extends Timestamps {
  id: ID;
  name: string;
  type: CampaignType;
  status: CampaignStatus;
  startDate: Date;
  endDate: Date;
  targetAudience: string[];
  budget?: number;
  currency?: Currency;
  clickCount: number;
  impressionCount: number;
  conversionCount: number;
  isActive: boolean;
}

export interface Coupon extends Timestamps {
  id: ID;
  code: string;
  name: MultiLanguageText;
  description: MultiLanguageText;
  discountType: 'percentage' | 'fixed_amount' | 'free_shipping';
  discountValue: number;
  currency?: Currency;
  minimumOrderAmount?: number;
  maximumDiscountAmount?: number;
  usageLimit?: number;
  usageCount: number;
  userUsageLimit?: number;
  validFrom: Date;
  validUntil: Date;
  applicableProducts?: ID[];
  applicableCategories?: string[];
  isActive: boolean;
}

export interface Banner extends Timestamps {
  id: ID;
  title: MultiLanguageText;
  imageUrl: string;
  linkUrl?: string;
  position: 'hero' | 'sidebar' | 'footer' | 'popup';
  priority: number;
  startDate: Date;
  endDate: Date;
  clickCount: number;
  impressionCount: number;
  isActive: boolean;
}

// ===== 倉庫・物流管理関連の型定義 =====
export interface Warehouse extends Timestamps {
  id: ID;
  name: string;
  code: string;
  address: Address;
  contactInfo: {
    managerName: string;
    phone: string;
    email: string;
  };
  operatingHours: string;
  capabilities: string[];
  isActive: boolean;
}

export interface WarehouseStock extends Timestamps {
  id: ID;
  warehouseId: ID;
  productId: ID;
  sku: string;
  quantity: number;
  reservedQuantity: number;
  availableQuantity: number;
  lowStockThreshold: number;
  zone?: string;
  bin?: string;
  lastStockUpdate: Date;
}

export interface StockTransfer extends Timestamps {
  id: ID;
  transferNumber: string;
  fromWarehouseId: ID;
  toWarehouseId: ID;
  productId: ID;
  quantity: number;
  status: 'pending' | 'in_transit' | 'received' | 'cancelled';
  requestedBy: ID;
  approvedBy?: ID;
  shippedDate?: Date;
  receivedDate?: Date;
  notes?: string;
}


// ===== 通知システム関連の型定義 =====
export enum NotificationType {
  EMAIL = 'email',
  SMS = 'sms',
  PUSH = 'push',
  IN_APP = 'in_app',
  WEBHOOK = 'webhook',
}

export enum NotificationPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent',
}

export interface NotificationTemplate extends Timestamps {
  id: ID;
  name: string;
  type: NotificationType;
  subject: MultiLanguageText;
  content: MultiLanguageText;
  variables: string[];
  isActive: boolean;
}

export interface NotificationQueue extends Timestamps {
  id: ID;
  templateId: ID;
  recipientId: ID;
  recipientType: 'user' | 'admin' | 'seller';
  type: NotificationType;
  priority: NotificationPriority;
  subject: string;
  content: string;
  variables: Record<string, unknown>;
  scheduledFor: Date;
  status: 'pending' | 'sent' | 'failed' | 'cancelled';
  attemptCount: number;
  lastAttempt?: Date;
  errorMessage?: string;
}

export interface UserNotificationPreference extends Timestamps {
  id: ID;
  userId: ID;
  emailEnabled: boolean;
  smsEnabled: boolean;
  pushEnabled: boolean;
  orderUpdates: boolean;
  promotionalEmails: boolean;
  securityAlerts: boolean;
  language: Language;
  timezone: string;
}

// ===== DB通知（notificationsテーブル、Buyer/Seller/Admin共通）関連の型定義 =====
export interface Notification {
  id: ID;
  userId: ID;
  role: 'buyer' | 'seller' | 'admin';
  title: string;
  message: string;
  templateKey: string;
  templateParams: Record<string, string>;
  type: 'info' | 'warning' | 'error' | 'success';
  link?: string;
  isRead: boolean;
  createdAt: string;
  updatedAt?: string;
  readAt?: string;
}

// ===== カスタマーサービス関連の型定義 =====
export enum TicketStatus {
  OPEN = 'open',
  IN_PROGRESS = 'in_progress',
  WAITING_BUYER = 'waiting_buyer',
  WAITING_INTERNAL = 'waiting_internal',
  RESOLVED = 'resolved',
  CLOSED = 'closed',
}

export enum TicketPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent',
}


// ===== 顧客管理関連の型定義 =====
export enum BuyerStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
  BANNED = 'banned'
}

export enum BuyerTier {
  BRONZE = 'bronze',
  SILVER = 'silver',
  GOLD = 'gold',
  PLATINUM = 'platinum',
  DIAMOND = 'diamond',
  VIP = 'vip',
  PREMIUM = 'premium',
  REGULAR = 'regular'
}

export interface Buyer extends Timestamps {
  id: ID;
  userId: ID;
  firstName: string;
  lastName: string;
  name?: string;
  email: string;
  phone?: string;
  phoneNumber?: string;
  avatar?: string;
  dateOfBirth?: Date;
  gender?: 'male' | 'female' | 'other';
  status: BuyerStatus;
  tier: BuyerTier;
  totalOrders: number;
  orderCount?: number;
  totalSpent: number;
  averageOrderValue: number;
  lastOrderDate?: Date;
  lastLoginDate?: Date;
  lastLoginAt?: Date;
  registrationDate: Date;
  registeredAt?: Date;
  preferredLanguage: Language;
  languagePreference?: Language;
  preferredCurrency: Currency;
  currencyPreference?: Currency;
  marketingOptIn: boolean;
  addresses: Address[];
  paymentMethods: PaymentMethod[];
  notes?: BuyerNote[];
  emailVerified?: boolean;
  phoneVerified?: boolean;
  twoFactorEnabled?: boolean;
  defaultAddressId?: ID;
  defaultPaymentMethodId?: ID;
  newsletterSubscribed?: boolean;
  marketingConsent?: boolean;
  dataRetentionConsent?: boolean;
  tags?: string[];
  customAttributes?: Record<string, unknown>;
}

export interface BuyerNote extends Timestamps {
  id: ID;
  buyerId: ID;
  authorId: ID;
  note: string;
  isInternal: boolean;
  tags: string[];
}

// ===== 監査・ログ関連の型定義 =====
export interface AuditLog extends Timestamps {
  id: ID;
  userId?: ID;
  userType: 'user' | 'admin' | 'seller' | 'system';
  action: string;
  entityType: string;
  entityId?: ID;
  oldValues?: Record<string, unknown>;
  newValues?: Record<string, unknown>;
  ipAddress?: string;
  userAgent?: string;
  metadata?: Record<string, unknown>;
}

export interface LoginAttempt extends Timestamps {
  id: ID;
  email: string;
  success: boolean;
  ipAddress: string;
  userAgent: string;
  failureReason?: string;
  userId?: ID;
}

export interface SecurityEvent extends Timestamps {
  id: ID;
  eventType: 'failed_login' | 'password_changed' | 'suspicious_activity' | 'account_locked';
  userId?: ID;
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  ipAddress?: string;
  userAgent?: string;
  resolved: boolean;
  resolvedBy?: ID;
  resolvedAt?: Date;
}

// ===== 翻訳管理関連の型定義 =====
export interface TranslationKey extends Timestamps {
  id: ID;
  key: string;
  namespace: string;
  description?: string;
  translations: Record<Language, string>;
  isActive: boolean;
  lastModified: Date;
}

export interface AutoTranslationLog extends Timestamps {
  id: ID;
  sourceLanguage: Language;
  targetLanguage: Language;
  sourceText: string;
  translatedText: string;
  provider: string;
  confidence?: number;
  cost?: number;
  isManuallyVerified: boolean;
}

// ===== 為替・通貨関連の型定義 =====
export interface ExchangeRate extends Timestamps {
  id: ID;
  fromCurrency: Currency;
  toCurrency: Currency;
  rate: number;
  date: Date;
  provider: string;
  isActive: boolean;
}

export interface CurrencySettings extends Timestamps {
  id: ID;
  currency: Currency;
  countryCode: string;
  symbol: string;
  decimalPlaces: number;
  isDefault: boolean;
  isActive: boolean;
}

// ===== コンテンツ管理関連の型定義 =====
export interface FAQ extends Timestamps {
  id: ID;
  question: MultiLanguageText;
  answer: MultiLanguageText;
  category: string;
  tags: string[];
  viewCount: number;
  helpfulCount: number;
  unhelpfulCount: number;
  sortOrder: number;
  isActive: boolean;
  difficulty?: 'beginner' | 'intermediate' | 'advanced';
  targetAudience?: 'buyer' | 'seller' | 'admin' | 'all';
  relatedFAQs?: ID[];
  relatedArticles?: ID[];
}

export interface HelpArticle extends Timestamps {
  id: ID;
  title: MultiLanguageText;
  content: MultiLanguageText;
  summary?: MultiLanguageText;
  category: string;
  tags: string[];
  author: ID;
  authorName?: string;
  lastEditedBy?: ID;
  viewCount: number;
  helpfulCount: number;
  unhelpfulCount: number;
  isPublished: boolean;
  publishedAt?: Date;
  difficulty?: 'beginner' | 'intermediate' | 'advanced';
  targetAudience?: 'buyer' | 'seller' | 'admin' | 'all';
  estimatedReadTime?: number; // minutes
  relatedArticles?: ID[];
  relatedFAQs?: ID[];
  attachments?: HelpArticleAttachment[];
}

export interface HelpArticleAttachment {
  id: ID;
  fileName: string;
  fileUrl: string;
  fileType: string;
  fileSize: number;
  description?: string;
}

export interface FAQCategory {
  id: ID;
  name: MultiLanguageText;
  description?: MultiLanguageText;
  icon?: string;
  parentId?: ID;
  level: number;
  sortOrder: number;
  isActive: boolean;
  targetAudience?: 'buyer' | 'seller' | 'admin' | 'all';
  children?: FAQCategory[];
}

export interface HelpSearchParams {
  query?: string;
  category?: string;
  tags?: string[];
  type?: 'faq' | 'article' | 'all';
  difficulty?: 'beginner' | 'intermediate' | 'advanced';
  language?: Language;
  sortBy?: 'relevance' | 'popularity' | 'newest' | 'oldest';
  page?: number;
  limit?: number;
}

export interface HelpSearchResult {
  items: (FAQ | HelpArticle)[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  categories: FAQCategory[];
  suggestedTags: string[];
}

export interface FAQVoteRequest {
  faqId: ID;
  helpful: boolean;
}

export interface HelpArticleVoteRequest {
  articleId: ID;
  helpful: boolean;
}

export interface ContactFormRequest {
  name: string;
  email: string;
  category: string;
  subject: string;
  message: string;
  attachments?: File[];
  relatedFAQId?: ID;
  relatedArticleId?: ID;
  language?: Language;
}

// ===== Webhook関連の型定義 =====
export interface WebhookEndpoint extends Timestamps {
  id: ID;
  url: string;
  events: string[];
  secret: string;
  isActive: boolean;
  headers?: Record<string, string>;
  lastSuccessful?: Date;
  failureCount: number;
}

export interface WebhookEvent extends Timestamps {
  id: ID;
  endpointId: ID;
  eventType: string;
  payload: Record<string, unknown>;
  responseCode?: number;
  responseBody?: string;
  status: 'pending' | 'sent' | 'failed';
  attemptCount: number;
  nextAttempt?: Date;
}

// ===== システム設定関連の型定義 =====
export interface SystemSettings extends Timestamps {
  id: ID;
  key: string;
  value: string;
  description?: string;
  dataType: 'string' | 'number' | 'boolean' | 'json';
  isPublic: boolean;
  category: string;
}

// ===== セッション関連の型定義 =====
export interface SessionData {
  userId: ID;
  role: UserRole;
  expiresAt: Date;
  ipAddress?: string;
  userAgent?: string;
}

// セッション情報（デバイス別管理用）
export interface SessionInfo {
  sessionId: ID;
  userId?: ID;
  role: UserRole;
  deviceInfo: {
    userAgent: string;
    platform?: string;
    browser?: string;
  };
  ipAddress: string;
  lastAccessAt: Date;
  expiresAt: Date;
  isActive: boolean;
}

// 権限チェック関連
export interface PermissionCheck {
  userId?: ID;
  role: UserRole;
  resource: string;
  action: string;
  context?: Record<string, unknown>;
}

export interface PermissionResult {
  allowed: boolean;
  reason?: string;
  restrictions?: Record<string, unknown>;
}

// OAuth連携関連（購入者のみ）
export interface OAuthProvider {
  provider: 'google' | 'line' | 'kakao';
  providerId: ID;
  email?: string;
  name?: string;
  avatar?: string;
}

export interface OAuthConnection extends Timestamps {
  id: ID;
  userId: ID;
  provider: OAuthProvider['provider'];
  providerId: ID;
  email?: string;
  isActive: boolean;
}

// 販売者申請関連
export interface SellerApplication extends Timestamps {
  id: ID;
  userId: ID;
  businessName: MultiLanguageText;
  businessType: 'corporation' | 'individual' | 'other';
  registrationNumber?: string;
  contactEmail: string;
  contactPhone: string;
  businessAddress: {
    country: string;
    state?: string;
    city: string;
    streetAddress: string;
    postalCode: string;
  };
  bankAccount: {
    bankName: string;
    accountNumber: string;
    accountHolder: string;
    swiftCode?: string;
  };
  documents: {
    businessLicense?: string;
    taxRegistration?: string;
    bankStatement?: string;
    identityDocument?: string;
  };
  status: 'pending' | 'reviewing' | 'approved' | 'rejected';
  reviewNotes?: string;
  reviewedBy?: ID;
  reviewedAt?: Date;
  submittedAt: Date;
}

// 認証設定関連
export interface AuthConfig {
  tokenExpiry: {
    guest: { access: string; refresh: string };
    buyer: { access: string; refresh: string };
    seller: { access: string; refresh: string };
    admin: { access: string; refresh: string };
    superAdmin: { access: string; refresh: string };
  };
  sessionLimits: {
    buyer: number;  // 5デバイスまで
    seller: number;    // 3デバイスまで
    admin: number;     // 1デバイスのみ
  };
  security: {
    maxLoginAttempts: number;      // 5回
    lockoutDuration: string;        // '30m'
    rateLimit: {
      perMinute: number;            // 5回/分
      perHour: number;              // 100回/時
    };
    passwordResetExpiry: string;   // '10m'
  };
  passwordPolicy: {
    minLength: number;
    requireNumbers: boolean;
    requireSymbols: boolean;
    requireUppercase: boolean;
    requireLowercase: boolean;
  };
}

// ===== 見積もり関連の型定義 =====
export enum EstimateType {
  PRODUCT_PURCHASE = 'product_purchase',
  PROXY_PURCHASE = 'proxy_purchase',
}

export enum EstimateStatus {
  DRAFT = 'draft',
  PENDING = 'pending',
  CALCULATING = 'calculating',
  READY = 'ready',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  COUNTER_OFFERED = 'counter_offered',
  EXPIRED = 'expired',
  CANCELLED = 'cancelled',
}

export interface EstimateItem {
  id: ID;
  productId?: ID;
  productName: string;
  productDescription?: string;
  externalUrl?: string;
  quantity: number;
  unitPrice?: number;
  estimatedPrice?: number;
  currency: Currency;
  images?: string[];
  specifications?: Record<string, string>;
  notes?: string;
}

export interface EstimateShipping {
  fromCountry: string;
  toCountry: string;
  shippingMethod: string;
  weight?: number;
  dimensions?: {
    width: number;
    height: number;
    depth: number;
  };
  estimatedCost?: number;
  estimatedDays?: number;
  trackingIncluded?: boolean;
  insuranceIncluded?: boolean;
}

export interface EstimatePricing {
  itemsSubtotal: number;
  domesticShipping?: number;
  proxyServiceFee?: number;
  internationalShipping: number;
  customsFee?: number;
  taxAmount?: number;
  totalAmount: number;
  currency: Currency;
  exchangeRate?: number;
  originalCurrency?: Currency;
  originalAmount?: number;
}

export interface EstimateNegotiation {
  id: ID;
  estimateId: ID;
  fromUserId: ID;
  fromUserType: 'buyer' | 'admin' | 'seller';
  toUserId: ID;
  toUserType: 'buyer' | 'admin' | 'seller';
  message: string;
  proposedChanges?: {
    pricing?: Partial<EstimatePricing>;
    shipping?: Partial<EstimateShipping>;
    items?: Partial<EstimateItem>[];
    deliveryDate?: Date;
  };
  attachments?: string[];
  isCounterOffer: boolean;
  parentNegotiationId?: ID;
  createdAt: Date;
  expiresAt?: Date;
}

export interface Estimate extends Timestamps {
  id: ID;
  estimateNumber: string;
  userId: ID;
  type: EstimateType;
  status: EstimateStatus;
  title: string;
  description?: string;
  items: EstimateItem[];
  shipping: EstimateShipping;
  pricing?: EstimatePricing;
  requestedDeliveryDate?: Date;
  estimatedDeliveryDate?: Date;
  validUntil?: Date;
  attachments?: string[];
  notes?: string;
  internalNotes?: string;
  negotiations?: EstimateNegotiation[];
  assignedToId?: ID;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  language: Language;
}

export interface CreateEstimateRequest {
  type: EstimateType;
  title: string;
  description?: string;
  items: Omit<EstimateItem, 'id'>[];
  shipping: EstimateShipping;
  requestedDeliveryDate?: Date;
  attachments?: File[];
  notes?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  language?: Language;
}

export interface UpdateEstimateRequest {
  title?: string;
  description?: string;
  items?: Omit<EstimateItem, 'id'>[];
  shipping?: EstimateShipping;
  requestedDeliveryDate?: Date;
  notes?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
}

export interface EstimateCalculationRequest {
  items: {
    productId?: ID;
    externalUrl?: string;
    quantity: number;
    estimatedPrice?: number;
  }[];
  shipping: Pick<EstimateShipping, 'fromCountry' | 'toCountry' | 'shippingMethod' | 'weight' | 'dimensions'>;
  currency: Currency;
}

export interface EstimateApprovalRequest {
  estimateId: ID;
  approved: boolean;
  message?: string;
  modifications?: {
    pricing?: Partial<EstimatePricing>;
    deliveryDate?: Date;
    notes?: string;
  };
}

export interface EstimateNegotiationRequest {
  estimateId: ID;
  message: string;
  proposedChanges?: {
    pricing?: Partial<EstimatePricing>;
    shipping?: Partial<EstimateShipping>;
    items?: Partial<EstimateItem>[];
    deliveryDate?: Date;
  };
  attachments?: File[];
  isCounterOffer?: boolean;
  expiresAt?: Date;
}

// ===== 外部サイト連携関連の型定義 =====
export enum ExternalSiteType {
  MERCARI = 'mercari',
  YAUCTION = 'yauction',
  RAKUTEN = 'rakuten',
  OTHER = 'other'
}

export enum SyncStatus {
  INACTIVE = 'inactive',
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  SUCCESS = 'success',
  PARTIAL_SUCCESS = 'partial_success',
  FAILED = 'failed'
}

export interface ExternalSite extends Timestamps {
  id: ID;
  type: ExternalSiteType;
  name: string;
  description?: string;
  isActive: boolean;
  apiSettings: {
    apiKey?: string;
    apiSecret?: string;
    appId?: string;
    shopId?: string;
    webhookUrl?: string;
    testMode?: boolean;
    [key: string]: string | boolean | undefined;
  };
  syncSettings?: {
    autoSync: boolean;
    syncInterval: 'realtime' | 'hourly' | 'twice_daily' | 'daily' | 'weekly';
    lastSyncAt?: Date;
    syncStatus: SyncStatus;
    includedCategories?: ID[];
    excludedProducts?: ID[];
  };
}

export interface PriceRule extends Timestamps {
  id: ID;
  externalSiteId: ID;
  name: string;
  markupType: 'percentage' | 'fixed';
  markupValue: number;
  currency: Currency;
  includeShipping: boolean;
  roundingRule?: 'no_rounding' | 'up_to_10' | 'up_to_100' | 'up_to_1000';
  minPrice?: number;
  maxPrice?: number;
  isActive: boolean;
}

export interface CategoryMapping extends Timestamps {
  id: ID;
  externalSiteId: ID;
  externalCategoryId: ID;
  externalCategoryName: string;
  internalCategoryId: ID;
  internalCategoryName: string;
  confidence: number;
  isManuallyMapped: boolean;
}

export interface SyncSchedule extends Timestamps {
  id: ID;
  externalSiteId: ID;
  frequency: 'realtime' | 'hourly' | 'twice_daily' | 'daily' | 'weekly';
  time: string[] | null;
  daysOfWeek?: number[];
  isActive: boolean;
  nextRunAt?: Date;
}

export interface SyncHistory extends Omit<Timestamps, 'updatedAt'> {
  id: ID;
  externalSiteId: ID;
  syncType: 'full' | 'incremental' | 'manual';
  status: SyncStatus;
  startedAt: Date;
  completedAt?: Date;
  itemsProcessed: number;
  itemsAdded: number;
  itemsUpdated: number;
  itemsFailed: number;
  errorMessages?: string[];
}

export interface SyncRule extends Timestamps {
  id: ID;
  externalSiteId: ID;
  name: string;
  ruleType: 'include' | 'exclude';
  field: string;
  operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'greater_than' | 'less_than' | 'is_empty' | 'is_not_empty';
  value: string;
  isActive: boolean;
}

// ===== SEO/OGP関連の型定義 =====

/**
 * SEO設定
 */
export interface SEOSettings extends Timestamps {
  id: ID;
  defaultTitle: string;
  defaultDescription: string;
  defaultKeywords: string[];
  robotsTxt: string;
  sitemapEnabled: boolean;
  analyticsId?: string;
}

/**
 * SEO設定更新リクエスト（全フィールド任意）
 */
export interface SEOSettingsUpdate {
  defaultTitle?: string;
  defaultDescription?: string;
  defaultKeywords?: string[];
  robotsTxt?: string;
  sitemapEnabled?: boolean;
  analyticsId?: string;
}

/**
 * OGP設定
 */
export interface OGPSettings extends Timestamps {
  id: ID;
  pageType: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  ogUrl: string;
  twitterCard: string;
}

/**
 * OGP設定作成リクエスト
 */
export interface OGPSettingsCreate {
  pageType: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  ogUrl: string;
  twitterCard?: string;
}

/**
 * OGP設定更新リクエスト（全フィールド任意）
 */
export interface OGPSettingsUpdate {
  pageType?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  twitterCard?: string;
}

/**
 * カテゴリー別売上データ
 */
export interface SalesByCategory {
  category: string;
  sales: number;
  count: number;
  percentage: number;
}

/**
 * 地域別売上データ
 */
export interface SalesByRegion {
  region: string;
  sales: number;
  count: number;
  percentage: number;
}

/**
 * 商品分析データ
 */
export interface ProductAnalytics {
  productId: ID;
  productName: string;
  views: number;
  sales: number;
  revenue: number;
  conversionRate: number;
}

/**
 * 商品パフォーマンスデータ
 */
export interface ProductPerformance {
  productId: ID;
  productName: string;
  category: string;
  totalSales: number;
  totalRevenue: number;
  averageRating: number;
  reviewCount: number;
  returnRate: number;
}

/**
 * ユーザー行動分析データ
 */
export interface UserBehaviorAnalytics {
  userId: ID;
  pageViews: number;
  sessionDuration: number;
  bounceRate: number;
  purchaseCount: number;
  averageOrderValue: number;
}

/**
 * ユーザーセグメンテーションデータ
 */
export interface UserSegmentation {
  segmentName: string;
  userCount: number;
  revenue: number;
  averageOrderValue: number;
  purchaseFrequency: number;
}

/**
 * コンバージョンファネルデータ
 */
export interface ConversionFunnel {
  stepName: string;
  stepOrder: number;
  userCount: number;
  conversionRate: number;
  dropoffRate: number;
}

/**
 * レポートデータ
 */
export interface Report extends Timestamps {
  id: ID;
  name: string;
  type: string;
  description?: string;
  parameters: Record<string, unknown>;
  createdBy: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  fileUrl?: string;
}

/**
 * レポート作成リクエスト
 */
export interface ReportRequest {
  name: string;
  type: string;
  description?: string;
  parameters: Record<string, unknown>;
  startDate?: string;
  endDate?: string;
}

/**
 * レポートスケジュール
 */
export interface ReportSchedule extends Timestamps {
  id: ID;
  reportType: string;
  name: string;
  frequency: 'daily' | 'weekly' | 'monthly';
  parameters: Record<string, unknown>;
  recipients: string[];
  isActive: boolean;
  lastRunAt?: string;
  nextRunAt: string;
}

/**
 * エクスポートリクエスト
 */
export interface ExportRequest {
  type: string;
  format: 'csv' | 'xlsx' | 'pdf';
  filters?: Record<string, unknown>;
  startDate?: string;
  endDate?: string;
}

/**
 * エクスポートジョブ
 */
export interface ExportJob extends Timestamps {
  id: ID;
  type: string;
  format: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  fileUrl?: string;
  expiresAt: string;
}

/**
 * アナリティクスダッシュボード
 */
export interface AnalyticsDashboard {
  salesAnalytics: SalesAnalytics;
  salesByCategory: SalesByCategory[];
  salesByRegion: SalesByRegion[];
  productPerformance: ProductPerformance[];
  conversionFunnel: ConversionFunnel[];
}

/**
 * アナリティクスフィルター
 */
export interface AnalyticsFilter {
  startDate?: string;
  endDate?: string;
  category?: string;
  region?: string;
  sellerId?: number;
  productId?: number;
}

/**
 * チャットセッション作成リクエスト
 */
export interface ChatSessionCreate {
  participantId: ID;
  type: 'buyer_seller' | 'buyer_support' | 'seller_support';
  subject?: string;
}

/**
 * チャットセッション一覧レスポンス
 */
export interface ChatSessionListResponse {
  sessions: ChatSession[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * チャットセッションフィルター
 */
export interface ChatSessionFilter {
  type?: 'buyer_seller' | 'buyer_support' | 'seller_support';
  status?: 'active' | 'archived' | 'closed';
  participantId?: number;
  page?: number;
  pageSize?: number;
}

/**
 * チャットメッセージ作成リクエスト
 */
export interface ChatMessageCreate {
  sessionId: ID;
  content: string;
  type?: ChatMessageType;
  attachments?: string[];
}

/**
 * チャットメッセージ一覧レスポンス
 */
export interface ChatMessageListResponse {
  messages: ChatMessage[];
  total: number;
  hasMore: boolean;
}

/**
 * チャットメッセージ既読マーク
 */
export interface ChatMessageMarkRead {
  messageIds: string[];
}

/**
 * サポートチケット作成リクエスト
 */
export interface SupportTicketCreate {
  subject: string;
  category: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  description: string;
  attachments?: string[];
}

/**
 * サポートチケット更新リクエスト
 */
export interface SupportTicketUpdate {
  status?: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  notes?: string;
}

/**
 * サポートチケット割り当てリクエスト
 */
export interface SupportTicketAssign {
  assigneeId: ID;
  notes?: string;
}

/**
 * サポートチケット一覧レスポンス
 */
export interface SupportTicketListResponse {
  tickets: SupportTicket[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * サポートチケットフィルター
 */
export interface SupportTicketFilter {
  status?: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  category?: string;
  assigneeId?: number;
  page?: number;
  pageSize?: number;
}

/**
 * 見積作成リクエスト
 */
export interface EstimateCreate {
  productUrl: string;
  quantity: number;
  destinationCountry: string;
  shippingMethod?: string;
  notes?: string;
  attachments?: string[];
}

/**
 * 見積更新リクエスト
 */
export interface EstimateUpdate {
  quantity?: number;
  destinationCountry?: string;
  shippingMethod?: string;
  notes?: string;
  status?: 'pending' | 'quoted' | 'accepted' | 'rejected' | 'expired';
}

/**
 * 見積レスポンス
 */
export interface EstimateResponse {
  estimate: Estimate;
  shippingMethods: ShippingMethod[];
  exchangeRate: number;
}

/**
 * 見積一覧レスポンス
 */
export interface EstimateListResponse {
  estimates: Estimate[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * 見積統計レスポンス
 */
export interface EstimateStatsResponse {
  totalEstimates: number;
  pendingEstimates: number;
  quotedEstimates: number;
  acceptedEstimates: number;
  rejectedEstimates: number;
  averageResponseTime: number;
}

/**
 * 配送方法レスポンス
 */
export interface ShippingMethodResponse {
  shippingMethods: ShippingMethod[];
  defaultMethod?: ShippingMethod;
}

/**
 * 配送先国レスポンス
 */
export interface DestinationCountryResponse {
  countries: Array<{
    code: string;
    name: string;
    nameEn: string;
    nameKo: string;
    isAvailable: boolean;
  }>;
}

/**
 * 為替レートレスポンス
 */
export interface ExchangeRateResponse {
  baseCurrency: string;
  targetCurrency: string;
  rate: number;
  updatedAt: string;
}

/**
 * 添付ファイル設定レスポンス
 */
export interface AttachmentSettingsResponse {
  maxFileSize: number;
  allowedFileTypes: string[];
  maxFilesPerEstimate: number;
}

/**
 * ファイルアップロードレスポンス
 */
export interface FileUploadResponse {
  fileId: ID;
  fileName: string;
  fileUrl: string;
  fileSize: number;
  fileType: string;
  uploadedAt: string;
}

/**
 * クーポン作成リクエスト
 */
export interface CreateCouponRequest {
  code: string;
  name: string;
  description?: string;
  type: 'percentage' | 'fixed';
  discountValue: number;
  minPurchaseAmount?: number;
  maxDiscountAmount?: number;
  validFrom: string;
  validUntil: string;
  usageLimit?: number;
  usageLimitPerUser?: number;
  applicableProducts?: number[];
  applicableCategories?: string[];
  isActive?: boolean;
}

/**
 * クーポン検証リクエスト
 */
export interface ValidateCouponRequest {
  code: string;
  orderId?: number;
  totalAmount: number;
}

/**
 * クーポン検証レスポンス
 */
export interface ValidateCouponResponse {
  isValid: boolean;
  coupon?: Coupon;
  discountAmount?: number;
  message?: string;
}

/**
 * キャンペーンデータ
 */
export interface Campaign extends Timestamps {
  id: ID;
  name: string;
  description?: string;
  slug: string;
  startDate: string;
  endDate: string;
  targetSegments?: Record<string, unknown>;
  targetUserTiers?: string[];
  targetCountries?: string[];
  couponIds?: string[];
  bannerImageUrl?: string;
  landingPageUrl?: string;
  targetRevenue?: number;
  targetOrders?: number;
  targetNewUsers?: number;
  content?: MultiLanguageText;
  status: 'draft' | 'scheduled' | 'active' | 'completed' | 'cancelled';
  createdBy?: number;
}

/**
 * キャンペーン作成リクエスト
 */
export interface CreateCampaignRequest {
  name: string;
  description: string;
  type: 'sale' | 'promotion' | 'event';
  startDate: string;
  endDate: string;
  bannerImage?: string;
  landingPageUrl?: string;
  targetProducts?: number[];
  discountRate?: number;
  budget?: number;
  isActive?: boolean;
  targetAudience?: string[];
  associatedCoupons?: number[];
}

/**
 * バナー作成リクエスト
 */
export interface CreateBannerRequest {
  title: string;
  name?: string;
  description?: string;
  imageUrl: string;
  image?: string;
  imageMobile?: string;
  linkUrl?: string;
  position: 'top' | 'middle' | 'bottom' | 'sidebar';
  displayOrder: number;
  startDate?: string;
  endDate?: string;
  isActive?: boolean;
}

/**
 * SEO設定（SeoSettings エイリアス）
 */
export type SeoSettings = SEOSettings;

/**
 * SEO設定作成リクエスト
 */
export interface CreateSeoSettingsRequest {
  defaultTitle: string;
  defaultDescription: string;
  defaultKeywords: string[];
  robotsTxt?: string;
  sitemapEnabled?: boolean;
  analyticsId?: string;
  pageType?: string;
  pageId?: string;
  title?: string;
  description?: string;
  keywords?: string[];
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  canonicalUrl?: string;
  noindex?: boolean;
  nofollow?: boolean;
  structuredData?: Record<string, unknown>;
}

/**
 * レビュー一覧レスポンス
 */
export interface ReviewsResponse {
  reviews: Review[];
  total: number;
  page: number;
  pageSize: number;
  averageRating?: number;
}

/**
 * レビュー投票リクエスト
 */
export interface ReviewVoteRequest {
  reviewId: ID;
  voteType?: 'helpful' | 'not_helpful';
  helpful?: boolean;
}

/**
 * レビュー報告リクエスト
 */
export interface ReviewReportRequest {
  reviewId: ID;
  reason: string;
  description?: string;
}

/**
 * ウィッシュリストアイテム
 */
export interface WishlistItem extends Timestamps {
  id: ID;
  userId: ID;
  productCode: string;
  product?: Product;
  notes?: string;
  priority?: 'low' | 'medium' | 'high';
  name: string;
  price: number;
  isProxyPurchase: boolean;
  productImage: string;
  productName: string;
  productPrice: number;
}



/**
 * 販売者アナリティクス（SellerAnalytics エイリアス）
 */
export type SellerAnalytics = SalesAnalytics;

/**
 * マーケティングダッシュボード統計
 */
export interface MarketingDashboardStats {
  totalCampaigns: number;
  activeCampaigns: number;
  totalBanners: number;
  activeBanners: number;
  totalCoupons: number;
  activeCoupons: number;
  couponUsageRate: number;
  campaignROI: number;
}

/**
 * マーケティングレポートフィルター
 */
export interface MarketingReportFilters {
  startDate?: string;
  endDate?: string;
  campaignId?: number;
  couponId?: string;
  bannerId?: number;
  type?: 'campaign' | 'coupon' | 'banner';
}



/**
 * 破損レポートリクエスト
 */
export interface DamageReportRequest {
  itemId: ID;
  damagedQuantity: number;
  description: string;
  images?: string[];
}

/**
 * 入庫レポートレスポンス
 */
export interface InboundReportResponse {
  reportId: ID;
  reportUrl: string;
  createdAt: string;
  summary: {
    totalItems: number;
    receivedItems: number;
    damagedItems: number;
    missingItems: number;
  };
}


/**
 * レビュー返信作成リクエスト
 */
export interface CreateReviewReplyRequest {
  reviewId: ID;
  comment: string;
}

// ========================================
// StaticContent（静的コンテンツ）
// ========================================

/**
 * 静的コンテンツ（公開用）
 * slug指定で取得する定型文・お知らせ等
 */
export interface StaticContent {
  id: ID;
  slug: string;
  order: number;
  description: string | null;
  title: MultiLanguageText;
  content: MultiLanguageText;
  image: string | null;
  status: 'draft' | 'published' | 'archived';
  publishedAt: string | null;
  expiredAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * StaticContent一覧レスポンス
 */
export type StaticContentListResponse = StaticContent[];

// ========================================
// 配送管理・入庫管理 型定義（Slice 3-C）
// ========================================

// ========================================
// グループ3-C-1: 配送方法・料金管理
// ========================================

export interface ShippingMethod {
  id: string;
  name: Record<string, string>;
  description: Record<string, string>;
  carrierId: string | null;
  carrierName: string | null;
  estimatedDaysMin: number;
  estimatedDaysMax: number;
  trackingSupported: boolean;
  insuranceIncluded: boolean;
  signatureRequired: boolean;
  basePrice: string;
  pricePerKg: string;
  currency: string;
  maxWeightKg: number;
  maxLengthCm: number | null;
  maxWidthCm: number | null;
  maxHeightCm: number | null;
  supportedCountries: string[];
  restrictedItems: string[];
  insuranceFee: string;
  signatureFee: string;
  isActive: boolean;
  displayScope: 'domestic' | 'international' | 'hidden';
  createdAt: string;
  updatedAt: string;
}

export interface ShippingMethodCreate {
  name: Record<string, string>;
  description: Record<string, string>;
  carrierId: string;
  estimatedDaysMin: number;
  estimatedDaysMax: number;
  trackingSupported: boolean;
  insuranceIncluded: boolean;
  signatureRequired: boolean;
  basePrice: string;
  pricePerKg: string;
  currency: string;
  maxWeightKg: number;
  maxLengthCm?: number | null;
  maxWidthCm?: number | null;
  maxHeightCm?: number | null;
  supportedCountries: string[];
  restrictedItems: string[];
  insuranceFee: string;
  signatureFee: string;
  isActive: boolean;
}

export interface ShippingMethodUpdate {
  name?: Record<string, string>;
  description?: Record<string, string>;
  carrierId?: string;
  estimatedDaysMin?: number;
  estimatedDaysMax?: number;
  basePrice?: string;
  pricePerKg?: string;
  maxWeightKg?: number;
  supportedCountries?: string[];
  isActive?: boolean;
}

export interface ShippingCalculateRequest {
  destinationCountry: string;
  weightKg: number;
  lengthCm?: number | null;
  widthCm?: number | null;
  heightCm?: number | null;
  insuranceRequired?: boolean;
  signatureRequired?: boolean;
}

export interface ShippingCalculateResponse {
  methodId: string;
  methodName: Record<string, string>;
  carrierName: Record<string, string>;
  estimatedDaysMin: number;
  estimatedDaysMax: number;
  basePrice: number;
  weightCharge: number;
  insuranceFee: number;
  signatureFee: number;
  totalPrice: number;
  currency: string;
  trackingSupported: boolean;
  warnings: string[] | null;
}

export interface Carrier {
  id: string;
  name: string;
  apiEndpoint: string;
  apiKeyEncrypted: string;
  trackingUrlTemplate: string;
  supportedCountries: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CarrierUpdate {
  apiEndpoint?: string;
  apiKey?: string;
  trackingUrlTemplate?: string;
  supportedCountries?: string[];
  isActive?: boolean;
}

// ========================================
// グループ3-C-2: 配送追跡・ラベル発行
// ========================================

export interface TrackingEvent {
  timestamp: string;
  status: string;
  location: string;
  description: string;
}

export interface TrackingResponse {
  trackingNumber: string;
  carrierName: string;
  currentStatus: string;
  estimatedDelivery: string | null;
  events: TrackingEvent[];
  trackingUrl: string;
  pollingInProgress: boolean;
}

export interface ShippingLabelRequest {
  orderId: string;
  shippingMethodId: string;
  senderAddress: {
    name: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    phone: string;
  };
  recipientAddress: {
    name: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    phone: string;
  };
  packageInfo: {
    weightKg: number;
    lengthCm: number;
    widthCm: number;
    heightCm: number;
  };
  insuranceRequired?: boolean;
  signatureRequired?: boolean;
}

export interface ShippingLabelResponse {
  labelId: string;
  trackingNumber: string;
  trackingUrl: string;
  labelUrl: string;
  carrierName: string;
  estimatedDelivery: string | null;
  customsDocuments: string[] | null;
  createdAt: string;
}

export interface CustomsDocumentRequest {
  orderId: string;
  recipientCountry: string;
  items: {
    description: string;
    quantity: number;
    unitValue: number;
    hsCode: string;
    originCountry: string;
  }[];
  totalValue: number;
  currency: string;
  shippingPurpose: 'sale' | 'gift' | 'sample' | 'return';
}

export interface CustomsDocumentResponse {
  documentId: string;
  orderId: string;
  documentType: string;
  documentUrl: string;
  createdAt: string;
}

// ========================================
// グループ3-C-3: 入庫管理
// ========================================

export type InboundStatus = 'planned' | 'in_transit' | 'received' | 'partial' | 'cancelled';

export interface InboundShipmentItem {
  productId: string;
  variantId?: string;
  sku: string;
  quantity: number;
}

export interface InboundShipment {
  id: string;
  userId: string;
  shipmentNumber: string;
  status: InboundStatus;
  originAddress: {
    name: string;
    addressLine1: string;
    city: string;
    postalCode: string;
    country: string;
  };
  carrierName: string | null;
  trackingNumber: string | null;
  trackingUrl: string | null;
  items: InboundShipmentItem[];
  totalItems: number;
  receivedItems: any[] | null;
  receivedTotal: number;
  expectedArrivalDate: string;
  actualArrivalDate: string | null;
  damageReport: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  receivedAt: string | null;
}

export interface InboundShipmentCreate {
  originAddress: {
    name: string;
    addressLine1: string;
    city: string;
    postalCode: string;
    country: string;
  };
  carrierName?: string | null;
  trackingNumber?: string | null;
  items: InboundShipmentItem[];
  expectedArrivalDate: string;
  notes?: string | null;
}

export interface InboundShipmentUpdate {
  status?: InboundStatus;
  carrierName?: string;
  trackingNumber?: string;
  expectedArrivalDate?: string;
  notes?: string;
}

export interface InboundShipmentReceiveRequest {
  receivedItems: {
    productId: string;
    receivedQuantity: number;
    condition: string;
    notes?: string;
  }[];
  actualArrivalDate: string;
  damageReport?: string;
  notes?: string;
}

export interface DamageReportRequest {
  reportedBy: string;
  damageDescription: string;
  damagedItems: {
    productId: string;
    quantity: number;
    damageType: string;
  }[];
  photos?: string[];
}

export interface InboundItemUpdateRequest {
  receivedQuantity: number;
  condition: string;
  notes?: string;
}

export interface InboundReportResponse {
  totalShipments: number;
  receivedShipments: number;
  inTransitShipments: number;
  totalItemsExpected: number;
  totalItemsReceived: number;
  damageReportsCount: number;
  periodStart: string;
  periodEnd: string;
}

/**
 * ページネーション型定義
 */
export interface PaginationRequestParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface PaginationResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PaginationProps {
  total: number;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
  onLimitChange?: (limit: number) => void;
  limitOptions?: number[];
  disabled?: boolean;
  compact?: boolean;
}

export const PAGINATION_DEFAULTS = {
  page: 1,
  limit: 20,
  maxLimit: 100,
  limitOptions: [5, 10, 25, 50, 100],
} as const;
